/* global Office, Excel, document, console, window, clearTimeout, setTimeout */

const CONFIG = {
  mainSheet: "REM",
  // Holds both the column metadata dictionary and the VBA command bridge cell.
  metaSheet: "_REM_Meta_Dict",
  commandCell: "C1",

  metadataRowIndex: 0,
  dataFirstRowIndex: 5,

  // В твоём файле дата реализации доставки визуально находится в строке 4 Excel,
  // потому что строка 1 — скрытая metadata row.
  deliveryDateRowIndex: 3,

  selectionDebounceMs: 180,

  defaultMonths: 12,
  defaultDetail: "month",
  defaultLookaheadMonths: 5,
  defaultLookaheadDetail: "month",
  defaultAggregation: "sum",
};

// Upper sanity bound when reading the global lead time out of the header band.
const MAX_LEAD_TIME_MONTHS = 36;

// A click on an Excel column header selects every row on the sheet. Reading
// that many products x ~480 columns in one sync freezes the pane, so refuse it
// and say so instead.
const MAX_SELECTED_PRODUCTS = 500;

const META_FIELD = {
  schemaVersion: 0,
  groupId: 1,
  roleId: 2,
  seriesId: 3,
  levelId: 4,
  year: 5,
  month: 6,
  weekOrOrder: 7,
  flags: 8,
  subgroupId: 9,
  uid: 10,
};

const GROUP_ID = {
  ZAMOWIENIA_ZAKUPU: 3,
  MAGAZYN: 6,
  SPRZEDAZ: 7,
  DNI_0: 8,
  PROGNOZA: 9,
  SPRZEDAZ_MARKETY: 10,
};

const ROLE_ID = {
  STOCK: 2,
  VALUE: 4,
  DELIVERY: 5,
};

const SERIES_ID = {
  SPRZEDAZ: 1,
  DNI_0: 2,
  PROGNOZA: 3,
  SPRZEDAZ_MARKETY: 4,
};

const LEVEL_ID = {
  MONTH: 2,
  WEEK: 3,
  DETAIL: 5,
};

const COLUMN_UID = {
  PRODUCT_NAME: "C0003",
  AVERAGE: "C0021",
  AVERAGE_CORRECTION: "C0022",
  LEAD_TIME_MONTHS: "C0026",
  PROPOSAL: "C0028",
  CURRENT_STOCK_6: "C0032",
};

let state = {
  columnMap: [],
  schemaFingerprint: "",
  lastCommandText: "",
  monthWindow: CONFIG.defaultMonths,
  detail: CONFIG.defaultDetail,
  lookaheadMonthWindow: CONFIG.defaultLookaheadMonths,
  lookaheadDetail: CONFIG.defaultLookaheadDetail,
  aggregation: CONFIG.defaultAggregation,
  showDeliveries: true,
  useForecastSource: true,
  lastModel: null,
  salesChart: null,
  lookaheadChart: null,
  forecastChart: null,
  selectionTimer: null,
  refreshRunId: 0,
  paneVisible: true,
  initialized: false,
  theme: getInitialTheme(),
  themeButtonsWired: false,
  hoverWired: false,
};

function MiniChart(canvas, config) {
  this.canvas = canvas;
  this.ctx = canvas.getContext("2d");
  this.config = config || {};
  this.options = this.config.options || {};
  this.scales = {};
  this.chartArea = { top: 0, right: 0, bottom: 0, left: 0 };
  this._resize = () => this.draw();

  MiniChart.instances.set(canvas, this);

  if (typeof window !== "undefined") {
    window.addEventListener("resize", this._resize);
  }

  this.draw();
}

MiniChart.instances = new WeakMap();
MiniChart.plugins = [];

MiniChart.register = function register(...plugins) {
  for (const plugin of plugins) {
    if (plugin && !MiniChart.plugins.includes(plugin)) {
      MiniChart.plugins.push(plugin);
    }
  }
};

MiniChart.getChart = function getChart(canvas) {
  return MiniChart.instances.get(canvas) || null;
};

MiniChart.prototype.destroy = function destroy() {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", this._resize);
  }

  MiniChart.instances.delete(this.canvas);
  this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
};

MiniChart.prototype.draw = function draw() {
  const size = resizeMiniChartCanvas(this.canvas, this.ctx);

  this.ctx.clearRect(0, 0, size.width, size.height);

  const padding = normalizeChartPadding(this.options.layout?.padding);
  const xOptions = this.options.scales?.x || {};
  const yOptions = this.options.scales?.y || {};
  const showX = xOptions.display !== false;
  const showY = yOptions.display !== false;
  const leftAxis = showY ? 34 : 6;
  const bottomAxis = showX ? 18 : 8;

  this.chartArea = {
    top: padding.top + 2,
    right: Math.max(padding.left + leftAxis + 20, size.width - padding.right - 6),
    bottom: Math.max(padding.top + 24, size.height - padding.bottom - bottomAxis),
    left: padding.left + leftAxis,
  };

  const dataRange = getMiniChartRange(this.config, this.options);
  const xMin = getFiniteNumber(xOptions.min, dataRange.xMin);
  const xMax = Math.max(xMin + 1, getFiniteNumber(xOptions.max, dataRange.xMax));
  // beginAtZero means "include zero in the range", not "clamp the floor to
  // zero": a month where returns exceed sales is a real negative, and dropping
  // it here drew the line below the axis and over the month labels.
  const yMin = getFiniteNumber(
    yOptions.min,
    yOptions.beginAtZero ? Math.min(0, dataRange.yMin) : dataRange.yMin
  );
  const yMax = Math.max(yMin + 1, getFiniteNumber(yOptions.max, dataRange.yMax));

  this.scales = {
    x: makeMiniScale(xMin, xMax, this.chartArea.left, this.chartArea.right),
    y: makeMiniScale(yMin, yMax, this.chartArea.bottom, this.chartArea.top),
  };

  drawMiniChartAxes(this, xOptions, yOptions);
  runMiniChartPlugins(this, "beforeDatasetsDraw");
  drawMiniChartDatasets(this);
  runMiniChartPlugins(this, "afterDatasetsDraw");
};

function getChartRuntime() {
  if (typeof window !== "undefined" && window.Chart) {
    return window.Chart;
  }

  return MiniChart;
}

function resizeMiniChartCanvas(canvas, ctx) {
  const parent = canvas.parentElement;
  const width = Math.max(1, parent?.clientWidth || canvas.clientWidth || 300);
  const height = Math.max(1, parent?.clientHeight || canvas.clientHeight || 180);
  const ratio = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const pixelWidth = Math.round(width * ratio);
  const pixelHeight = Math.round(height * ratio);

  canvas.style.width = "100%";
  canvas.style.height = "100%";

  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
  }

  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

  return { width, height };
}

function normalizeChartPadding(padding) {
  if (typeof padding === "number") {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }

  return {
    top: padding?.top || 0,
    right: padding?.right || 0,
    bottom: padding?.bottom || 0,
    left: padding?.left || 0,
  };
}

function getMiniChartRange(config, options) {
  const values = [];
  const datasets = config.data?.datasets || [];

  for (const dataset of datasets) {
    for (const point of dataset.data || []) {
      values.push({ x: toNumber(point.x), y: toNumber(point.y) });
    }
  }

  for (const series of options.plugins?.salesLine?.series || []) {
    for (let i = 0; i < (series.values || []).length; i++) {
      values.push({ x: i, y: toNumber(series.values[i]) });
    }
  }

  for (const marker of options.plugins?.deliveryMarker?.markers || []) {
    values.push({ x: toNumber(marker.x), y: toNumber(marker.y) });
  }

  const xs = values.map((point) => point.x).filter(Number.isFinite);
  const ys = values.map((point) => point.y).filter(Number.isFinite);
  const xMin = xs.length ? Math.min(...xs) : 0;
  const xMax = xs.length ? Math.max(...xs) : 1;
  const yMin = ys.length ? Math.min(0, Math.min(...ys)) : 0;
  const rawYMax = ys.length ? Math.max(...ys) : 1;
  const yMax = rawYMax <= 0 ? 1 : rawYMax * 1.12;

  return { xMin, xMax, yMin, yMax };
}

function getFiniteNumber(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

function makeMiniScale(min, max, pixelMin, pixelMax) {
  return {
    min,
    max,
    getPixelForValue(value) {
      const ratio = (toNumber(value) - min) / (max - min);

      return pixelMin + ratio * (pixelMax - pixelMin);
    },
  };
}

function drawMiniChartAxes(chart, xOptions, yOptions) {
  const ctx = chart.ctx;
  const area = chart.chartArea;

  ctx.save();
  ctx.textBaseline = "middle";

  if (yOptions.display !== false) {
    ctx.font = getMiniTickFont(yOptions.ticks);

    const ticks = buildMiniTicks(
      chart.scales.y.min,
      chart.scales.y.max,
      yOptions.ticks?.maxTicksLimit || 5
    );

    ctx.textAlign = "right";
    ctx.fillStyle = yOptions.ticks?.color || "#64748b";
    ctx.strokeStyle = yOptions.grid?.color || "rgba(148, 163, 184, 0.22)";
    ctx.lineWidth = 1;

    for (const tick of ticks) {
      const y = chart.scales.y.getPixelForValue(tick);

      if (yOptions.grid?.display !== false) {
        ctx.beginPath();
        ctx.moveTo(area.left, y);
        ctx.lineTo(area.right, y);
        ctx.stroke();
      }

      const label = formatMiniTick(tick, yOptions.ticks);
      ctx.fillText(label, area.left - (yOptions.ticks?.padding || 4), y);
    }
  }

  if (xOptions.display !== false) {
    ctx.font = getMiniTickFont(xOptions.ticks);

    const ticks = buildMiniXTicks(
      chart.scales.x.min,
      chart.scales.x.max,
      xOptions.ticks?.stepSize || 1
    );
    const tickItems = ticks
      .map((tick) => {
        const x = chart.scales.x.getPixelForValue(tick);
        const label = formatMiniTick(tick, xOptions.ticks);

        return {
          x,
          label,
        };
      })
      .filter(
        (item) =>
          item.label &&
          Number.isFinite(item.x) &&
          item.x >= area.left - 1 &&
          item.x <= area.right + 1
      );
    const visibleTicks =
      xOptions.ticks?.autoSkip === false
        ? tickItems
        : autoSkipMiniTickLabels(ctx, tickItems, area, xOptions.ticks);

    if (xOptions.grid?.display !== false) {
      ctx.strokeStyle = xOptions.grid?.color || "rgba(148, 163, 184, 0.18)";
      ctx.lineWidth = 1;

      for (const item of visibleTicks) {
        ctx.beginPath();
        ctx.moveTo(item.x, area.top);
        ctx.lineTo(item.x, area.bottom);
        ctx.stroke();
      }
    }

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = xOptions.ticks?.color || "#64748b";

    for (const item of visibleTicks) {
      ctx.fillText(item.label, item.x, area.bottom + (xOptions.ticks?.padding || 4));
    }
  }

  ctx.restore();
}

function buildMiniTicks(min, max, maxTicks) {
  const count = Math.max(2, maxTicks || 5);
  const step = getNiceStep((max - min) / Math.max(1, count - 1));
  const first = Math.ceil(min / step) * step;
  const ticks = [];

  for (let value = first; value <= max + step * 0.25; value += step) {
    ticks.push(normalizeTickValue(value));
  }

  if (!ticks.includes(0) && min <= 0 && max >= 0) {
    ticks.unshift(0);
  }

  return ticks.slice(0, count + 1);
}

function buildMiniXTicks(min, max, step) {
  const cleanStep = Math.max(1, step || 1);
  const ticks = [];

  for (let value = Math.ceil(min); value <= max + 0.001; value += cleanStep) {
    ticks.push(normalizeTickValue(value));
  }

  return ticks;
}

function getMiniTickFont(ticksOptions) {
  const size = Number.isFinite(ticksOptions?.font?.size) ? ticksOptions.font.size : 9;

  return `${size}px Segoe UI, Arial, sans-serif`;
}

function autoSkipMiniTickLabels(ctx, items, area, ticksOptions) {
  if (items.length <= 1) {
    return items;
  }

  const optionLimit = Number.isFinite(ticksOptions?.maxTicksLimit)
    ? Math.max(2, ticksOptions.maxTicksLimit)
    : items.length;
  const labelGap = Number.isFinite(ticksOptions?.labelGap) ? ticksOptions.labelGap : 6;
  const leftLimit = area.left - 2;
  const rightLimit = area.right + 2;
  const visible = [];
  let lastRight = -Infinity;

  for (const item of items) {
    if (visible.length >= optionLimit) {
      break;
    }

    const labelWidth = ctx.measureText(item.label).width;
    const left = item.x - labelWidth / 2;
    const right = item.x + labelWidth / 2;

    if (right < leftLimit || left > rightLimit) {
      continue;
    }

    if (left >= lastRight + labelGap) {
      visible.push(item);
      lastRight = right;
    }
  }

  return visible.length ? visible : [items[0]];
}

function getNiceStep(rawStep) {
  if (!Number.isFinite(rawStep) || rawStep <= 0) {
    return 1;
  }

  const power = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const scaled = rawStep / power;
  const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;

  return nice * power;
}

function normalizeTickValue(value) {
  return Math.abs(value) < 0.000001 ? 0 : Number(value.toFixed(6));
}

function formatMiniTick(value, ticksOptions) {
  if (typeof ticksOptions?.callback === "function") {
    return String(ticksOptions.callback(value));
  }

  return formatNumber(value);
}

function runMiniChartPlugins(chart, hookName) {
  const pluginOptions = chart.options.plugins || {};

  for (const plugin of MiniChart.plugins) {
    if (typeof plugin[hookName] !== "function") {
      continue;
    }

    plugin[hookName](chart, {}, pluginOptions[plugin.id] || {});
  }
}

function drawMiniChartDatasets(chart) {
  const datasets = chart.config.data?.datasets || [];

  chart.ctx.save();
  clipToChartArea(chart);

  for (const dataset of datasets) {
    if (dataset.showLine === false || !dataset.data?.length) {
      continue;
    }

    drawMiniChartDataset(chart, dataset);
  }

  chart.ctx.restore();
}

// Curve tension overshoots past the extreme data points, so a spike can throw
// control points outside the plot box. Chart.js clips its datasets; without the
// same here the overshoot is painted across the axis labels and out of the card.
// Only the data drawing is clipped — the delivery badges are docked in the
// padding above the plot box on purpose.
function clipToChartArea(chart) {
  const area = chart.chartArea;

  chart.ctx.beginPath();
  chart.ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
  chart.ctx.clip();
}

function drawMiniChartDataset(chart, dataset) {
  const ctx = chart.ctx;
  const points = dataset.data
    .map((point) => ({
      x: chart.scales.x.getPixelForValue(point.x),
      y: chart.scales.y.getPixelForValue(point.y),
      raw: point,
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

  if (points.length < 2) {
    return;
  }

  ctx.save();

  if (dataset.fill) {
    ctx.fillStyle = dataset.backgroundColor || "rgba(79, 70, 229, 0.08)";
    ctx.beginPath();
    ctx.moveTo(points[0].x, chart.chartArea.bottom);

    for (const point of points) {
      ctx.lineTo(point.x, point.y);
    }

    ctx.lineTo(points[points.length - 1].x, chart.chartArea.bottom);
    ctx.closePath();
    ctx.fill();
  }

  ctx.lineWidth = dataset.borderWidth || 2.4;
  ctx.lineCap = dataset.borderCapStyle || "round";
  ctx.lineJoin = dataset.borderJoinStyle || "round";

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];

    ctx.strokeStyle = getMiniSegmentColor(dataset, p0.raw, p1.raw);
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.stroke();
  }

  ctx.restore();
}

function getMiniSegmentColor(dataset, p0, p1) {
  if (typeof dataset.segment?.borderColor === "function") {
    return dataset.segment.borderColor({
      p0: { parsed: { x: p0.x, y: p0.y } },
      p1: { parsed: { x: p1.x, y: p1.y } },
    });
  }

  return dataset.borderColor || "rgb(79, 70, 229)";
}

const ChartRuntime = getChartRuntime();

const salesLinePlugin = {
  id: "salesLine",

  afterDatasetsDraw(chart, args, pluginOptions) {
    const series = pluginOptions?.series || [];

    if (!series.length) {
      return;
    }

    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;

    ctx.save();
    clipToChartArea(chart);

    for (const item of series) {
      drawSalesLine(ctx, xScale, yScale, item);
    }

    ctx.restore();
  },
};

const monthGridPlugin = {
  id: "monthGrid",

  beforeDatasetsDraw(chart, args, pluginOptions) {
    const positions = pluginOptions?.positions || [];

    if (!positions.length) {
      return;
    }

    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const { top, bottom, left, right } = chart.chartArea;
    const palette = getThemePalette();

    ctx.save();
    ctx.strokeStyle = palette.monthGrid;
    ctx.lineWidth = 1;

    for (const position of positions) {
      const x = xScale.getPixelForValue(position);

      if (!Number.isFinite(x) || x < left || x > right) {
        continue;
      }

      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const deliveryMarkerPlugin = {
  id: "deliveryMarker",

  afterDatasetsDraw(chart, args, pluginOptions) {
    const markers = pluginOptions?.markers || [];

    if (!markers.length) {
      return;
    }

    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    const chartArea = chart.chartArea;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "700 9.5px Segoe UI, Arial, sans-serif";

    const markerLayout = markers
      .map((marker) => ({
        ...marker,
        pixelX: xScale.getPixelForValue(marker.x),
        pixelY: yScale.getPixelForValue(marker.y),
      }))
      .filter(
        (marker) =>
          Number.isFinite(marker.pixelX) &&
          Number.isFinite(marker.pixelY) &&
          marker.pixelX >= chartArea.left &&
          marker.pixelX <= chartArea.right
      )
      .sort((a, b) => a.pixelX - b.pixelX);

    const maxLanes = pluginOptions?.maxLanes || 2;
    const dockTop = pluginOptions?.dockTop !== false;
    const laneRightEdges = Array.from({ length: maxLanes }, () => -Infinity);
    const laneHeight = 19;

    for (const marker of markerLayout) {
      const x = marker.pixelX;
      const y = marker.pixelY;

      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        continue;
      }

      if (x < chartArea.left || x > chartArea.right) {
        continue;
      }

      const text = formatNumber(marker.qty);
      const colors = getDeliveryMarkerColors(marker.kind);
      const paddingX = 5;
      const boxH = 17;
      const textW = ctx.measureText(text).width;
      const boxW = Math.max(24, textW + paddingX * 2);

      let boxX = x - boxW / 2;

      if (boxX < chartArea.left) {
        boxX = chartArea.left;
      }

      if (boxX + boxW > chartArea.right) {
        boxX = chartArea.right - boxW;
      }

      let lane = laneRightEdges.findIndex((rightEdge) => boxX > rightEdge + 4);

      if (lane < 0) {
        // Every lane is still occupied at this x. Use the one that frees up
        // soonest, so the unavoidable overlap is the smallest possible —
        // always stacking on the last lane hid the badge already drawn there.
        lane = laneRightEdges.reduce(
          (best, rightEdge, index) => (rightEdge < laneRightEdges[best] ? index : best),
          0
        );
      }

      laneRightEdges[lane] = Math.max(laneRightEdges[lane], boxX + boxW);

      const boxY = dockTop
        ? 4 + lane * laneHeight
        : Math.max(4, chartArea.top - 7 - boxH - lane * laneHeight);
      const stemTop = boxY + boxH;
      const stemBottom = y - 4;

      if (stemBottom > stemTop) {
        ctx.strokeStyle = colors.stem;
        ctx.lineWidth = 1.2;
        ctx.lineCap = "round";

        ctx.beginPath();
        ctx.moveTo(x, stemTop);
        ctx.lineTo(x, stemBottom);
        ctx.stroke();
      }

      ctx.fillStyle = colors.dot;
      ctx.beginPath();
      ctx.arc(x, y, 3.2, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = colors.fill;
      ctx.strokeStyle = colors.stroke;
      ctx.lineWidth = 1;

      roundRect(ctx, boxX, boxY, boxW, boxH, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = colors.text;
      ctx.fillText(text, boxX + boxW / 2, boxY + boxH / 2 + 0.5);
    }

    ctx.restore();
  },
};

ChartRuntime.register(salesLinePlugin, monthGridPlugin, deliveryMarkerPlugin);

onDomReady(() => {
  applyTheme(state.theme);
  wireThemeButtons();

  if (typeof Office === "undefined") {
    setStatus("Office.js unavailable. Open this pane from Excel.");
  }
});

if (typeof Office !== "undefined") {
  Office.onReady(startOfficePane);
}

function onDomReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
    return;
  }

  callback();
}

function getInitialTheme() {
  try {
    const saved = window.localStorage?.getItem("remTaskpaneTheme");

    if (saved === "dark" || saved === "light") {
      return saved;
    }
  } catch {
    // Ignore storage restrictions inside older Office WebView hosts.
  }

  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function wireThemeButtons() {
  if (state.themeButtonsWired) {
    return;
  }

  const lightButton = document.getElementById("btnThemeLight");
  const darkButton = document.getElementById("btnThemeDark");

  if (!lightButton || !darkButton) {
    return;
  }

  state.themeButtonsWired = true;

  lightButton.addEventListener("click", () => {
    setTheme("light");
  });
  darkButton.addEventListener("click", () => {
    setTheme("dark");
  });

  updateThemeButtons();
}

function setTheme(theme) {
  state.theme = theme === "dark" ? "dark" : "light";

  try {
    window.localStorage?.setItem("remTaskpaneTheme", state.theme);
  } catch {
    // The theme still applies for the current session if storage is unavailable.
  }

  applyTheme(state.theme);
  updateThemeButtons();

  if (state.lastModel) {
    renderSalesChart(state.lastModel);
    renderLookaheadChart(state.lastModel.lookahead);
    renderForecastChart(state.lastModel.forecast);
  }
}

function applyTheme(theme) {
  document.body?.setAttribute("data-theme", theme === "dark" ? "dark" : "light");
}

function updateThemeButtons() {
  const lightButton = document.getElementById("btnThemeLight");
  const darkButton = document.getElementById("btnThemeDark");

  if (!lightButton || !darkButton) {
    return;
  }

  lightButton.classList.toggle("active", state.theme === "light");
  darkButton.classList.toggle("active", state.theme === "dark");
  lightButton.setAttribute("aria-pressed", String(state.theme === "light"));
  darkButton.setAttribute("aria-pressed", String(state.theme === "dark"));
}

function getThemePalette() {
  if (state.theme === "dark") {
    return {
      axis: "#94a3b8",
      xGrid: "rgba(148, 163, 184, 0.14)",
      yGrid: "rgba(148, 163, 184, 0.18)",
      monthGrid: "rgba(148, 163, 184, 0.2)",
    };
  }

  return {
    axis: "#64748b",
    xGrid: "rgba(148, 163, 184, 0.16)",
    yGrid: "rgba(148, 163, 184, 0.22)",
    monthGrid: "rgba(148, 163, 184, 0.26)",
  };
}

async function startOfficePane(info) {
  if (info.host !== Office.HostType.Excel) {
    setStatus("Open in Excel.");
    return;
  }

  wireButtons();
  wireProductTitleScroll();
  wireChartHover();

  try {
    await ensureStartupBehavior();
    await initVisibilityTracking();
    await loadSchema();
    await registerSelectionChanged();
    await registerCommandBridge();

    state.initialized = true;

    await refreshFromCurrentSelection();
    setStatus("Ready.");
  } catch (error) {
    setStatus("Init error: " + formatError(error));
  }
}

function wireButtons() {
  document.getElementById("btn8").addEventListener("click", async () => {
    await setMonthWindow(8);
  });

  document.getElementById("btn12").addEventListener("click", async () => {
    await setMonthWindow(12);
  });

  document.getElementById("btn16").addEventListener("click", async () => {
    await setMonthWindow(16);
  });

  document.getElementById("btn20").addEventListener("click", async () => {
    await setMonthWindow(20);
  });

  document.getElementById("btnM").addEventListener("click", async () => {
    await setDetail("month");
  });

  document.getElementById("btnW").addEventListener("click", async () => {
    await setDetail("week");
  });

  document.getElementById("btnAgr").addEventListener("click", async () => {
    await toggleAggregation();
  });

  document.getElementById("btnDelivery").addEventListener("click", async () => {
    await toggleDeliveryMarkers();
  });

  document.getElementById("btnRefresh").addEventListener("click", async () => {
    // loadSchema has no catch of its own, unlike refreshFromCurrentSelection.
    // Without this the button users press when they suspect stale data fails
    // as an unobserved rejection and does nothing visible at all.
    try {
      await loadSchema({ force: true });
    } catch (error) {
      setStatus("Schema error: " + formatError(error));
      return;
    }

    await refreshFromCurrentSelection();
  });

  document.getElementById("btnLookahead2").addEventListener("click", async () => {
    await setLookaheadMonthWindow(2);
  });

  document.getElementById("btnLookahead5").addEventListener("click", async () => {
    await setLookaheadMonthWindow(5);
  });

  document.getElementById("btnLookahead7").addEventListener("click", async () => {
    await setLookaheadMonthWindow(7);
  });

  document.getElementById("btnLookahead9").addEventListener("click", async () => {
    await setLookaheadMonthWindow(9);
  });

  document.getElementById("btnLookaheadM").addEventListener("click", async () => {
    await setLookaheadDetail("month");
  });

  document.getElementById("btnLookaheadW").addEventListener("click", async () => {
    await setLookaheadDetail("week");
  });

  document.getElementById("btnProg").addEventListener("click", async () => {
    await toggleForecastSource();
  });
}

function wireProductTitleScroll() {
  const el = document.getElementById("productTitle");

  el.addEventListener(
    "wheel",
    (event) => {
      if (!event.shiftKey) {
        return;
      }

      event.preventDefault();
      el.scrollLeft += event.deltaY;
    },
    { passive: false }
  );
}

async function ensureStartupBehavior() {
  try {
    await Office.addin.setStartupBehavior(Office.StartupBehavior.load);
  } catch (error) {
    console.warn("Startup behavior unavailable", error);
  }
}

async function initVisibilityTracking() {
  try {
    const mode = await Office.addin.getVisibilityMode();
    state.paneVisible = mode === Office.VisibilityMode.taskpane;

    await Office.addin.onVisibilityModeChanged((args) => {
      state.paneVisible = args.visibilityMode === Office.VisibilityMode.taskpane;

      if (state.paneVisible) {
        refreshFromCurrentSelection();
      }
    });
  } catch (error) {
    console.warn("Visibility API unavailable", error);
  }
}

async function loadSchema(options) {
  const force = options?.force === true;

  await Excel.run(async (context) => {
    const ws = context.workbook.worksheets.getItem(CONFIG.mainSheet);
    const used = ws.getUsedRange();
    // Same range as getRangeByIndexes(metadataRowIndex, 0, 1, columnCount) —
    // row 1 column A always carries metadata, so the used range starts at A1 —
    // but taken off `used` it needs no column count, which keeps the whole
    // cache check inside the single sync below.
    const metadataRow = used.getRow(CONFIG.metadataRowIndex);

    used.load(["columnCount"]);
    metadataRow.load(["values", "text"]);
    await context.sync();

    // Reuse the cached schema only while the metadata row is byte-for-byte the
    // one it was built from. Column count alone is not enough: a regeneration
    // that keeps the same width but reorders the blocks, or inserts one column
    // and deletes another, shifts every colIndex past the edit. The panel would
    // then read each series out of its neighbour's column with nothing to show
    // that anything was wrong. Re-reading one row per selection is cheap; the
    // dictionary and the header band — the part that made the panel sluggish —
    // are still only touched when the fingerprint actually changes.
    const fingerprint = buildSchemaFingerprint(metadataRow.text?.[0]);

    if (!force && state.columnMap.length && state.schemaFingerprint === fingerprint) {
      return;
    }

    const metadataByUid = await loadMetadataDictionary(context);
    const headerRows = ws.getRangeByIndexes(1, 0, 4, used.columnCount);

    headerRows.load(["values", "text"]);
    await context.sync();

    const schema = buildColumnMapFromMetadataRow(
      metadataRow.values[0],
      metadataRow.text[0],
      metadataByUid
    );

    applyForecastHeaderMetadata(schema.columns, headerRows.text, headerRows.values);

    state.columnMap = schema.columns;
    state.schemaFingerprint = fingerprint;

    setStatus(
      `Schema: ${state.columnMap.length} cols.${
        schema.duplicateCount ? ` ${schema.duplicateCount} duplicate IDs skipped.` : ""
      }`
    );
  });
}

// Cheap content hash of the metadata row. Any reorder, insert, delete or rolled
// time band changes it, which is exactly when the cached column map goes stale.
function buildSchemaFingerprint(rowTexts) {
  if (!Array.isArray(rowTexts)) {
    return "";
  }

  let hash = 5381;

  for (const cell of rowTexts) {
    const text = String(cell === null || cell === undefined ? "" : cell);

    for (let i = 0; i < text.length; i++) {
      hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
    }

    hash = ((hash * 33) ^ 31) >>> 0; // cell separator
  }

  return `${rowTexts.length}:${hash}`;
}

async function loadMetadataDictionary(context) {
  try {
    const ws = context.workbook.worksheets.getItem(CONFIG.metaSheet);
    const used = ws.getUsedRange();

    used.load(["values", "text"]);
    await context.sync();

    return buildMetadataDictionaryFromCells(used.values, used.text);
  } catch (error) {
    console.warn("Metadata dictionary unavailable", error);
    return new Map();
  }
}

function buildMetadataDictionaryFromCells(values, texts) {
  const result = new Map();
  const rowCount = Math.max(values?.length || 0, texts?.length || 0);

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    const valueRow = values?.[rowIndex] || [];
    const textRow = texts?.[rowIndex] || [];
    const colCount = Math.max(valueRow.length, textRow.length);

    for (let colIndex = 0; colIndex < colCount; colIndex++) {
      const meta = resolveInlineMetadata(valueRow[colIndex], textRow[colIndex]);

      if (!isValidColumnMetadata(meta)) {
        continue;
      }

      const uid = normalizeColumnUid(meta.uid);

      if (uid && !result.has(uid)) {
        result.set(uid, meta);
      }
    }
  }

  return result;
}

function buildColumnMapFromMetadataRow(rowValues, rowTexts, metadataByUid) {
  const columns = [];
  const seenUids = new Set();
  const duplicateUids = new Set();
  const colCount = Math.max(rowValues?.length || 0, rowTexts?.length || 0);

  for (let colIndex = 0; colIndex < colCount; colIndex++) {
    const meta = resolveColumnMetadata(rowValues?.[colIndex], rowTexts?.[colIndex], metadataByUid);

    if (!isValidColumnMetadata(meta)) {
      continue;
    }

    const uid = normalizeColumnUid(meta.uid);

    if (seenUids.has(uid)) {
      duplicateUids.add(uid);
      continue;
    }

    seenUids.add(uid);

    columns.push({
      colIndex,
      excelCol: colIndex + 1,
      uid,
      meta: {
        ...meta,
        uid,
      },
    });
  }

  return {
    columns,
    duplicateCount: duplicateUids.size,
  };
}

function applyForecastHeaderMetadata(columns, headerTexts, headerValues) {
  const forecastCols = columns
    .filter(
      (col) =>
        toNumber(col.meta.groupId) === GROUP_ID.PROGNOZA &&
        toNumber(col.meta.seriesId) === SERIES_ID.PROGNOZA
    )
    .sort((a, b) => a.colIndex - b.colIndex);

  if (!forecastCols.length) {
    return;
  }

  const yearRow = fillHeaderRowAcross(getHeaderRow(headerTexts, 2), getHeaderRow(headerValues, 2));
  const monthRows = [
    fillHeaderRowAcross(getHeaderRow(headerTexts, 3), getHeaderRow(headerValues, 3)),
    fillHeaderRowAcross(getHeaderRow(headerTexts, 4), getHeaderRow(headerValues, 4)),
  ];
  const forecastMonthCols = [];

  for (const col of forecastCols) {
    if (toNumber(col.meta.levelId) !== LEVEL_ID.MONTH) {
      continue;
    }

    const year = parseHeaderYear(yearRow[col.colIndex]);
    const month = parseHeaderMonth(monthRows, col.colIndex);

    if (!year || !month) {
      continue;
    }

    col.meta.year = year;
    col.meta.month = month;
    forecastMonthCols.push(col);
  }

  if (!forecastMonthCols.length) {
    return;
  }

  for (const col of forecastCols) {
    if (toNumber(col.meta.levelId) !== LEVEL_ID.WEEK) {
      continue;
    }

    const parentMonth = forecastMonthCols.find((candidate) => candidate.colIndex > col.colIndex);

    if (!parentMonth) {
      continue;
    }

    col.meta.year = parentMonth.meta.year;
    col.meta.month = parentMonth.meta.month;
    col.meta.weekOrOrder =
      parseHeaderWeekOrder(getHeaderCell(headerTexts, 4, col.colIndex)) || col.meta.weekOrOrder;
  }
}

function fillHeaderRowAcross(textRow, valueRow) {
  const maxLength = Math.max(textRow?.length || 0, valueRow?.length || 0);
  const result = [];
  let last = "";

  for (let index = 0; index < maxLength; index++) {
    const value = String(textRow?.[index] || valueRow?.[index] || "").trim();

    if (value) {
      last = value;
    }

    result[index] = last;
  }

  return result;
}

function getHeaderCell(rows, rowIndex, colIndex) {
  return String(getHeaderRow(rows, rowIndex)[colIndex] || "").trim();
}

function parseHeaderYear(value) {
  const match = String(value || "").match(/\b(20\d{2})\b/);

  return match ? Number(match[1]) : 0;
}

function parseHeaderMonth(rows, colIndex) {
  for (const row of rows) {
    const text = String(row?.[colIndex] || "").trim();
    const match = text.match(/\b(0?[1-9]|1[0-2])\b/);

    if (match) {
      return Number(match[1]);
    }
  }

  return 0;
}

function parseHeaderWeekOrder(value) {
  const match = String(value || "").match(/\bW?\s*(\d{1,2})\b/i);

  return match ? Number(match[1]) : 0;
}

function resolveColumnMetadata(rawValue, textValue, metadataByUid) {
  const inlineMeta = resolveInlineMetadata(rawValue, textValue);

  if (isValidColumnMetadata(inlineMeta)) {
    return inlineMeta;
  }

  const uid = extractColumnUid(rawValue, textValue);

  if (!uid) {
    return {};
  }

  return metadataByUid.get(uid) || {};
}

function resolveInlineMetadata(rawValue, textValue) {
  const valueMeta = parseMetadataCell(rawValue);

  if (isValidColumnMetadata(valueMeta)) {
    return valueMeta;
  }

  const textMeta = parseMetadataCell(textValue);

  if (isValidColumnMetadata(textMeta)) {
    return textMeta;
  }

  return {};
}

function isValidColumnMetadata(meta) {
  return toNumber(meta?.schemaVersion) === 1 && normalizeColumnUid(meta.uid) !== "";
}

function extractColumnUid(rawValue, textValue) {
  const candidates = [rawValue, textValue];

  for (const candidate of candidates) {
    const meta = parseMetadataCell(candidate);
    const metaUid = normalizeColumnUid(meta.uid);

    if (metaUid) {
      return metaUid;
    }

    const uid = normalizeColumnUid(candidate);

    if (uid) {
      return uid;
    }
  }

  return "";
}

function normalizeColumnUid(value) {
  const text = String(value || "").trim();

  if (!text || text.includes("=") || /\s/.test(text)) {
    return "";
  }

  return text.toUpperCase();
}

function parseMetadataCell(rawValue) {
  const text = String(rawValue || "").trim();
  const fields = {};

  if (!text) {
    return {};
  }

  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

  for (const line of lines) {
    const p = line.indexOf("=");

    if (p <= 0) {
      continue;
    }

    const keyText = line.slice(0, p).trim();
    const valueText = line.slice(p + 1).trim();
    const key = Number(keyText);

    if (!Number.isInteger(key)) {
      continue;
    }

    if (valueText === "") {
      fields[key] = "";
    } else if (/^-?\d+$/.test(valueText)) {
      fields[key] = Number(valueText);
    } else {
      fields[key] = valueText;
    }
  }

  return {
    schemaVersion: fields[META_FIELD.schemaVersion],
    groupId: fields[META_FIELD.groupId],
    roleId: fields[META_FIELD.roleId],
    seriesId: fields[META_FIELD.seriesId],
    levelId: fields[META_FIELD.levelId],
    year: fields[META_FIELD.year],
    month: fields[META_FIELD.month],
    weekOrOrder: fields[META_FIELD.weekOrOrder],
    flags: fields[META_FIELD.flags],
    subgroupId: fields[META_FIELD.subgroupId],
    uid: fields[META_FIELD.uid],
  };
}

async function registerSelectionChanged() {
  await Excel.run(async (context) => {
    context.workbook.onSelectionChanged.add(() => {
      debounceSelectionRefresh();
    });

    await context.sync();
  });
}

// Event-driven command bridge (no polling). The workbook VBA writes
// "Taskpanel=<command>;seq=<nonce>" into the meta sheet command cell on each
// Panel-button click — the nonce always changes, so Excel fires onChanged. We
// listen to that single event, read the cell, and act. Visibility comes from
// initVisibilityTracking (state.paneVisible), kept current by Office's
// onVisibilityModeChanged. We seed lastCommandText with the cell's current
// value so a stale command (or an unrelated meta-sheet edit) is never replayed.
async function registerCommandBridge() {
  try {
    await Excel.run(async (context) => {
      const ws = context.workbook.worksheets.getItem(CONFIG.metaSheet);
      const cell = ws.getRange(CONFIG.commandCell);

      cell.load("values");
      ws.onChanged.add(onBridgeChanged);
      await context.sync();

      state.lastCommandText = String(cell.values[0][0] || "").trim();
    });
  } catch (error) {
    console.warn("Command bridge unavailable", error);
  }
}

async function onBridgeChanged() {
  let command = null;

  try {
    await Excel.run(async (context) => {
      const cell = context.workbook.worksheets
        .getItem(CONFIG.metaSheet)
        .getRange(CONFIG.commandCell);

      cell.load("values");
      await context.sync();

      const text = String(cell.values[0][0] || "").trim();

      if (!text || text === state.lastCommandText) {
        return;
      }

      state.lastCommandText = text;
      command = parseBridgeCommand(text);
    });

    if (command) {
      await runBridgeCommand(command);
    }
  } catch (error) {
    // setStatus is invisible while the pane is hidden, so log too.
    console.warn("Bridge error", error);
    setStatus("Bridge error: " + formatError(error));
  }
}

function parseBridgeCommand(text) {
  const match = /^taskpanel\s*=\s*([^;]+)/i.exec(text);

  return match ? match[1].trim().toLowerCase() : null;
}

async function runBridgeCommand(command) {
  switch (command) {
    case "toggle":
      // Decide from the LIVE visibility mode, not the cached flag:
      // onVisibilityModeChanged does not reliably fire on a programmatic
      // hide(), so a cached flag gets stuck on "visible" and every Toggle then
      // hides — the pane closes but never reopens. Querying the real mode fixes
      // that.
      if (await isPaneVisible()) {
        await hidePaneNow();
      } else {
        await showPaneNow();
      }
      break;

    case "show":
    case "open":
      await showPaneNow();
      break;

    case "hide":
    case "close":
      await hidePaneNow();
      break;

    case "reload":
      await loadSchema({ force: true });
      await refreshFromCurrentSelection();
      break;

    default:
      // Unknown commands are ignored on purpose; the bridge only owns the pane.
      break;
  }
}

async function isPaneVisible() {
  try {
    return (await Office.addin.getVisibilityMode()) === Office.VisibilityMode.taskpane;
  } catch {
    return state.paneVisible;
  }
}

async function showPaneNow() {
  await Office.addin.showAsTaskpane();
  state.paneVisible = true;
  await refreshFromCurrentSelection();
}

async function hidePaneNow() {
  await Office.addin.hide();
  state.paneVisible = false;
}

function debounceSelectionRefresh() {
  if (state.selectionTimer) {
    clearTimeout(state.selectionTimer);
  }

  state.selectionTimer = setTimeout(() => {
    refreshFromCurrentSelection();
  }, CONFIG.selectionDebounceMs);
}

async function setMonthWindow(months) {
  state.monthWindow = months;
  updateButtons();
  await refreshFromCurrentSelection();
}

async function setDetail(detail) {
  state.detail = detail;
  updateButtons();
  await refreshFromCurrentSelection();
}

async function setLookaheadMonthWindow(months) {
  state.lookaheadMonthWindow = months;
  updateButtons();
  await refreshFromCurrentSelection();
}

async function setLookaheadDetail(detail) {
  state.lookaheadDetail = detail;
  updateButtons();
  await refreshFromCurrentSelection();
}

async function toggleAggregation() {
  state.aggregation = state.aggregation === "sum" ? "avg" : "sum";
  updateButtons();
  await refreshFromCurrentSelection();
}

async function toggleDeliveryMarkers() {
  state.showDeliveries = !state.showDeliveries;
  updateButtons();

  if (state.lastModel) {
    renderSalesChart(state.lastModel);
    renderLookaheadChart(state.lastModel.lookahead);
    setStatus(state.showDeliveries ? "Dostawy włączone." : "Dostawy ukryte.");
    return;
  }

  await refreshFromCurrentSelection();
}

async function toggleForecastSource() {
  state.useForecastSource = !state.useForecastSource;
  updateButtons();
  await refreshFromCurrentSelection();
}

function updateButtons() {
  document.getElementById("btn8").classList.toggle("active", state.monthWindow === 8);
  document.getElementById("btn12").classList.toggle("active", state.monthWindow === 12);
  document.getElementById("btn16").classList.toggle("active", state.monthWindow === 16);
  document.getElementById("btn20").classList.toggle("active", state.monthWindow === 20);

  document.getElementById("btnM").classList.toggle("active", state.detail === "month");
  document.getElementById("btnW").classList.toggle("active", state.detail === "week");

  document
    .getElementById("btnLookahead2")
    .classList.toggle("active", state.lookaheadMonthWindow === 2);
  document
    .getElementById("btnLookahead5")
    .classList.toggle("active", state.lookaheadMonthWindow === 5);
  document
    .getElementById("btnLookahead7")
    .classList.toggle("active", state.lookaheadMonthWindow === 7);
  document
    .getElementById("btnLookahead9")
    .classList.toggle("active", state.lookaheadMonthWindow === 9);
  document
    .getElementById("btnLookaheadM")
    .classList.toggle("active", state.lookaheadDetail === "month");
  document
    .getElementById("btnLookaheadW")
    .classList.toggle("active", state.lookaheadDetail === "week");
  const progButton = document.getElementById("btnProg");
  progButton.classList.toggle("active", state.useForecastSource);
  progButton.setAttribute("aria-pressed", String(state.useForecastSource));

  document.getElementById("btnAgr").textContent = "Agr.";
  document.getElementById("btnAgr").classList.toggle("active", state.aggregation === "avg");

  const deliveryButton = document.getElementById("btnDelivery");
  deliveryButton.classList.toggle("active", state.showDeliveries);
  deliveryButton.setAttribute("aria-pressed", String(state.showDeliveries));
}

async function refreshFromCurrentSelection() {
  const runId = ++state.refreshRunId;

  try {
    await loadSchema();

    if (runId !== state.refreshRunId) {
      return;
    }

    await Excel.run(async (context) => {
      const activeWs = context.workbook.worksheets.getActiveWorksheet();
      const selected = context.workbook.getSelectedRange();

      activeWs.load("name");
      selected.load(["rowIndex", "rowCount", "address"]);
      await context.sync();

      if (runId !== state.refreshRunId) {
        return;
      }

      if (activeWs.name !== CONFIG.mainSheet) {
        renderEmpty("Open REM sheet.");
        return;
      }

      const ws = activeWs;
      const used = ws.getUsedRange();

      used.load(["rowCount"]);
      await context.sync();

      if (runId !== state.refreshRunId) {
        return;
      }

      const productRowStart = Math.max(selected.rowIndex, CONFIG.dataFirstRowIndex);
      const productRowEnd = Math.min(selected.rowIndex + selected.rowCount - 1, used.rowCount - 1);

      if (productRowEnd < CONFIG.dataFirstRowIndex || productRowStart > productRowEnd) {
        renderEmpty("Select product rows.");
        return;
      }

      const selectedProductCount = productRowEnd - productRowStart + 1;

      if (selectedProductCount > MAX_SELECTED_PRODUCTS) {
        renderEmpty(
          `Zaznaczono ${formatNumber(selectedProductCount)} wierszy — max ${MAX_SELECTED_PRODUCTS}.`
        );
        return;
      }

      const selectedMonths = getLastMonths(state.monthWindow);
      const periods =
        state.detail === "week"
          ? getWeekPeriodsForMonths(selectedMonths)
          : getMonthPeriods(selectedMonths);
      const lookaheadMonths = getForecastMonths(state.lookaheadMonthWindow);
      const lookaheadPeriods =
        state.lookaheadDetail === "week"
          ? getForecastWeekPeriodsForMonths(lookaheadMonths)
          : getForecastMonthPeriods(lookaheadMonths);

      if (!periods.length) {
        renderEmpty("No periods.");
        return;
      }

      const allNeededCols = collectNeededColumns(periods, lookaheadPeriods);

      if (!allNeededCols.length) {
        renderEmpty("No data columns.");
        return;
      }

      const firstCol = Math.min(...allNeededCols.map((c) => c.colIndex));
      const lastCol = Math.max(...allNeededCols.map((c) => c.colIndex));
      const colCount = lastCol - firstCol + 1;

      const dataRange = ws.getRangeByIndexes(
        productRowStart,
        firstCol,
        selectedProductCount,
        colCount
      );

      const headerRowsRange = ws.getRangeByIndexes(1, firstCol, 3, colCount);

      dataRange.load("values");
      headerRowsRange.load(["values", "text"]);
      await context.sync();

      if (runId !== state.refreshRunId) {
        return;
      }

      const model = buildChartModel({
        matrix: dataRange.values,
        firstCol,
        selectedProductCount,
        periods,
        lookaheadPeriods,
        headerValues: headerRowsRange.values,
        headerTexts: headerRowsRange.text,
      });

      renderModel(model);
    });
  } catch (error) {
    // Every other exit from this function clears the pane. Without the same
    // here, a failed read leaves the previous product's title, KPI chips and
    // charts on screen labelled as the row the user just clicked.
    if (runId !== state.refreshRunId) {
      return;
    }

    renderEmpty("Read error: " + formatError(error));
  }
}

function getLastMonths(monthCount) {
  const monthCols = state.columnMap.filter((col) => {
    const m = col.meta;

    return (
      toNumber(m.groupId) === GROUP_ID.SPRZEDAZ &&
      toNumber(m.seriesId) === SERIES_ID.SPRZEDAZ &&
      toNumber(m.levelId) === LEVEL_ID.MONTH &&
      isValidPeriodMeta(m)
    );
  });

  const monthMap = new Map();

  for (const col of monthCols) {
    const year = toNumber(col.meta.year);
    const month = toNumber(col.meta.month);
    const key = monthKey(year, month);

    if (!monthMap.has(key)) {
      monthMap.set(key, {
        key,
        year,
        month,
      });
    }
  }

  return Array.from(monthMap.values())
    .sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month))
    .slice(-monthCount);
}

function getMonthPeriods(months) {
  return months.map((m) => ({
    type: "month",
    key: m.key,
    year: m.year,
    month: m.month,
    label: `${String(m.month).padStart(2, "0")}.${String(m.year).slice(-2)}`,
  }));
}

function getWeekPeriodsForMonths(months) {
  const monthSet = new Set(months.map((m) => m.key));

  const weekCols = state.columnMap.filter((col) => {
    const m = col.meta;

    return (
      toNumber(m.groupId) === GROUP_ID.SPRZEDAZ &&
      toNumber(m.seriesId) === SERIES_ID.SPRZEDAZ &&
      toNumber(m.levelId) === LEVEL_ID.WEEK &&
      isValidPeriodMeta(m) &&
      monthSet.has(monthKey(toNumber(m.year), toNumber(m.month)))
    );
  });

  return buildIsoWeekPeriods(weekCols);
}

function getForecastMonths(monthCount) {
  const availableMonths = getAvailableForecastMonths();

  if (availableMonths.length) {
    return availableMonths.slice(0, monthCount);
  }

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const months = [];

  for (let i = 0; i < monthCount; i++) {
    const date = addMonths(start, i);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    months.push({
      key: monthKey(year, month),
      year,
      month,
    });
  }

  return months;
}

// Forecast months come back in calendar order, never in physical column order.
// The workbook now writes the prognoza block newest-first, the same way the
// Sprzedaż / Dni z 0 / Markety blocks have always been written, so reading it
// left to right walks backwards through time. Sorting by year+month keeps the
// look-ahead correct whichever direction the generator lays the block out.
//
// Months that already ended are dropped: the look-ahead starts from the current
// stock, so it must start at the current month. A workbook that only carries
// past forecast months keeps them all rather than rendering nothing.
function getAvailableForecastMonths() {
  const monthCols = state.columnMap.filter((col) => {
    const m = col.meta;

    return (
      toNumber(m.groupId) === GROUP_ID.PROGNOZA &&
      toNumber(m.seriesId) === SERIES_ID.PROGNOZA &&
      toNumber(m.levelId) === LEVEL_ID.MONTH &&
      isValidPeriodMeta(m)
    );
  });

  const monthMap = new Map();

  for (const col of monthCols) {
    const year = toNumber(col.meta.year);
    const month = toNumber(col.meta.month);
    const key = monthKey(year, month);

    if (!monthMap.has(key)) {
      monthMap.set(key, {
        key,
        year,
        month,
      });
    }
  }

  const months = Array.from(monthMap.values()).sort(
    (a, b) => a.year * 12 + a.month - (b.year * 12 + b.month)
  );

  const now = new Date();
  const currentIndex = now.getFullYear() * 12 + now.getMonth() + 1;
  const upcoming = months.filter((m) => m.year * 12 + m.month >= currentIndex);

  return upcoming.length ? upcoming : months;
}

function getForecastMonthPeriods(months) {
  return months.map((m) => ({
    type: "month",
    key: m.key,
    year: m.year,
    month: m.month,
    label: `${String(m.month).padStart(2, "0")}.${String(m.year).slice(-2)}`,
  }));
}

function getForecastWeekPeriodsForMonths(months) {
  const monthSet = new Set(months.map((m) => m.key));

  const weekCols = state.columnMap.filter((col) => {
    const m = col.meta;

    return (
      toNumber(m.groupId) === GROUP_ID.PROGNOZA &&
      toNumber(m.seriesId) === SERIES_ID.PROGNOZA &&
      toNumber(m.levelId) === LEVEL_ID.WEEK &&
      isValidPeriodMeta(m) &&
      monthSet.has(monthKey(toNumber(m.year), toNumber(m.month)))
    );
  });

  const isoPeriods = buildIsoWeekPeriods(weekCols);

  if (isoPeriods.length) {
    return isoPeriods;
  }

  // No weekly forecast columns at all -> synthesise even weeks per month so the
  // look-ahead still has a weekly grid to consume against.
  const generated = [];

  for (const month of months) {
    generated.push(...generateForecastWeekPeriodsForMonth(month));
  }

  return generated;
}

function generateForecastWeekPeriodsForMonth(month) {
  const daysInMonth = new Date(month.year, month.month, 0).getDate();
  const weekCount = Math.ceil(daysInMonth / 7);
  const periods = [];

  for (let weekOrder = 1; weekOrder <= weekCount; weekOrder++) {
    periods.push({
      type: "week",
      key: `${month.year}-${String(month.month).padStart(2, "0")}-FW${weekOrder}`,
      isoYear: month.year,
      year: month.year,
      month: month.month,
      weekOrder,
      label: `W${weekOrder}`,
      // No week-level prognoza column can match this period; the value comes
      // from the month column instead (see buildForecastSourceValues).
      synthetic: true,
    });
  }

  return periods;
}

function collectNeededColumns(periods, lookaheadPeriods) {
  const needed = [];
  const productNameCol = findProductNameColumn();

  if (productNameCol) {
    needed.push(productNameCol);
  }

  for (const period of periods) {
    needed.push(...findValueColumns(GROUP_ID.SPRZEDAZ, SERIES_ID.SPRZEDAZ, period));
    needed.push(...findValueColumns(GROUP_ID.SPRZEDAZ_MARKETY, SERIES_ID.SPRZEDAZ_MARKETY, period));
    needed.push(...findValueColumns(GROUP_ID.DNI_0, SERIES_ID.DNI_0, period));
  }

  if (state.detail === "month") {
    const monthSet = new Set(periods.map((p) => monthKey(p.year, p.month)));

    const weeklyD0 = state.columnMap.filter((col) => {
      const m = col.meta;

      return (
        toNumber(m.groupId) === GROUP_ID.DNI_0 &&
        toNumber(m.seriesId) === SERIES_ID.DNI_0 &&
        toNumber(m.levelId) === LEVEL_ID.WEEK &&
        isValidPeriodMeta(m) &&
        monthSet.has(monthKey(toNumber(m.year), toNumber(m.month)))
      );
    });

    needed.push(...weeklyD0);
  }

  const deliveryCols = state.columnMap.filter(isDeliveryColumn);
  needed.push(...deliveryCols);

  for (const period of lookaheadPeriods) {
    needed.push(...findValueColumns(GROUP_ID.PROGNOZA, SERIES_ID.PROGNOZA, period));
  }

  needed.push(...state.columnMap.filter(isCurrentStockColumn));
  needed.push(...state.columnMap.filter(isFutureWarehouseOrderColumn));
  needed.push(...state.columnMap.filter(isWyliczeniaColumn));

  const byCol = new Map();

  for (const col of needed) {
    byCol.set(col.colIndex, col);
  }

  return Array.from(byCol.values());
}

// All value columns for a period. A month period matches a single column; a
// merged ISO-week period can match two columns (the partial halves of a week
// the workbook split across a month boundary), which the caller then sums.
function findValueColumns(groupId, seriesId, period) {
  return state.columnMap.filter((col) => columnMatchesValuePeriod(col, groupId, seriesId, period));
}

function columnMatchesValuePeriod(col, groupId, seriesId, period) {
  const m = col.meta;

  if (toNumber(m.groupId) !== groupId || toNumber(m.seriesId) !== seriesId) {
    return false;
  }

  if (toNumber(m.roleId) !== ROLE_ID.VALUE) {
    return false;
  }

  if (period.type === "month") {
    return (
      toNumber(m.levelId) === LEVEL_ID.MONTH &&
      toNumber(m.year) === period.year &&
      toNumber(m.month) === period.month
    );
  }

  if (period.type === "week") {
    if (toNumber(m.levelId) !== LEVEL_ID.WEEK) {
      return false;
    }

    const week = normalizeWeekOrder(m.weekOrOrder);

    return (
      week === period.weekOrder &&
      columnIsoWeekYear(toNumber(m.year), toNumber(m.month), week) === period.isoYear
    );
  }

  return false;
}

// Sum a period's value columns. For a merged ISO week this adds the two partial
// halves back into one full-week value; for months it is just the single
// column. Works for both "sum" and "avg" modes because the halves cover
// disjoint days of the same products.
function aggregateValueColumns(matrix, cols, firstCol, selectedProductCount, mode) {
  let total = 0;

  for (const col of cols) {
    total += aggregateColumn(matrix, col, firstCol, selectedProductCount, mode);
  }

  return total;
}

function isDeliveryColumn(col) {
  const m = col.meta;

  return (
    toNumber(m.groupId) === GROUP_ID.ZAMOWIENIA_ZAKUPU &&
    toNumber(m.roleId) === ROLE_ID.DELIVERY &&
    toNumber(m.levelId) === LEVEL_ID.DETAIL &&
    toNumber(m.subgroupId) === 2
  );
}

function isCurrentStockColumn(col) {
  const m = col.meta;

  return (
    toNumber(m.groupId) === GROUP_ID.MAGAZYN &&
    toNumber(m.roleId) === ROLE_ID.STOCK &&
    toNumber(m.subgroupId) === 4
  );
}

function isFutureWarehouseOrderColumn(col) {
  const m = col.meta;

  return (
    toNumber(m.groupId) === GROUP_ID.MAGAZYN &&
    toNumber(m.roleId) === ROLE_ID.DELIVERY &&
    toNumber(m.levelId) === LEVEL_ID.DETAIL &&
    toNumber(m.subgroupId) === 5
  );
}

function isWyliczeniaColumn(col) {
  return toNumber(col.meta.groupId) === 4;
}

function findColumnByUid(uid) {
  const normalizedUid = normalizeColumnUid(uid);

  return state.columnMap.find(
    (col) => normalizeColumnUid(col.uid || col.meta?.uid) === normalizedUid
  );
}

function findProductNameColumn() {
  return findColumnByUid(COLUMN_UID.PRODUCT_NAME);
}

function getSelectedProductName(matrix, firstCol) {
  const nameCol = findProductNameColumn();

  if (!nameCol) {
    return "";
  }

  const offset = nameCol.colIndex - firstCol;

  if (offset < 0) {
    return "";
  }

  return String(matrix?.[0]?.[offset] || "").trim();
}

function buildChartModel({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  lookaheadPeriods,
  headerValues,
  headerTexts,
}) {
  const singleProduct = selectedProductCount === 1;
  const firstName = getSelectedProductName(matrix, firstCol);

  const title = singleProduct
    ? firstName
    : `${aggregationLabel()}: ${selectedProductCount} towarów`;

  const sales = [];
  const market = [];
  const stockoutValues = [];

  for (const period of periods) {
    const salesCols = findValueColumns(GROUP_ID.SPRZEDAZ, SERIES_ID.SPRZEDAZ, period);
    const marketCols = findValueColumns(
      GROUP_ID.SPRZEDAZ_MARKETY,
      SERIES_ID.SPRZEDAZ_MARKETY,
      period
    );
    sales.push(
      aggregateValueColumns(matrix, salesCols, firstCol, selectedProductCount, state.aggregation)
    );
    market.push(
      aggregateValueColumns(matrix, marketCols, firstCol, selectedProductCount, state.aggregation)
    );
    stockoutValues.push(aggregateStockoutDays(matrix, firstCol, selectedProductCount, period));
  }

  const stockoutIntervals =
    state.detail === "week"
      ? buildWeeklyStockoutIntervals(periods, stockoutValues)
      : buildMonthlyStockoutIntervals(
          periods,
          matrix,
          firstCol,
          selectedProductCount,
          stockoutValues
        );

  const deliveries = buildDeliveryMarkers({
    matrix,
    firstCol,
    selectedProductCount,
    periods,
    sales,
    deliveryDateValues: getHeaderRow(headerValues, CONFIG.deliveryDateRowIndex),
    deliveryDateTexts: getHeaderRow(headerTexts, CONFIG.deliveryDateRowIndex),
  });
  const lookahead = buildLookaheadModel({
    matrix,
    firstCol,
    selectedProductCount,
    periods: lookaheadPeriods,
    headerValues,
    headerTexts,
  });
  const forecast = buildForecastChartModel({
    matrix,
    firstCol,
    selectedProductCount,
    periods: lookaheadPeriods,
  });

  const kpis = computeKpis({
    matrix,
    firstCol,
    selectedProductCount,
    periods,
    stockoutValues,
    headerValues,
    forecast,
  });

  return {
    title,
    periods,
    labels: periods.map((p) => p.label),
    sales,
    market,
    stockoutIntervals,
    deliveries,
    lookahead,
    forecast,
    kpis,
  };
}

// Per-selection summary numbers shown as chips above the charts: current stock,
// the monthly average, how many months that stock covers, the proposed reorder,
// and the share of zero-stock days over the visible window.
//
// "Śr./mc" and the coverage that derives from it follow the Prog. button: with
// Prog. OFF they use the workbook's historical average (C0021/C0022); with
// Prog. ON they use the forecast — the average per month of the prognoza shown
// on the third chart — so coverage reflects projected demand instead of past
// sales. Forecast falls back to history when the product has no forecast.
function computeKpis({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  stockoutValues,
  headerValues,
  forecast,
}) {
  const mode = state.aggregation;
  const stockCol = findColumnByUid(COLUMN_UID.CURRENT_STOCK_6);
  const averageCol = findColumnByUid(COLUMN_UID.AVERAGE);
  const correctionCol = findColumnByUid(COLUMN_UID.AVERAGE_CORRECTION);
  const proposalCol = findColumnByUid(COLUMN_UID.PROPOSAL);

  const stock = aggregateColumn(matrix, stockCol, firstCol, selectedProductCount, mode);

  const historicalAvg = aggregateAverageFallbackColumn(
    matrix,
    correctionCol,
    averageCol,
    firstCol,
    selectedProductCount,
    mode
  );

  const forecastAvg = state.useForecastSource ? forecastMonthlyAverage(forecast) : 0;
  const avgPerMonth = state.useForecastSource && forecastAvg > 0 ? forecastAvg : historicalAvg;
  const avgIsForecast = state.useForecastSource && forecastAvg > 0;

  const proposalQty = aggregateColumn(matrix, proposalCol, firstCol, selectedProductCount, mode);
  const leadTime = getLeadTimeMonths(headerValues, firstCol);
  const proposalDate = proposalQty > 0 && leadTime > 0 ? addMonths(new Date(), leadTime) : null;
  const coverageMonths = avgPerMonth > 0 ? stock / avgPerMonth : null;

  let zeroDays = 0;
  let totalDays = 0;

  for (let i = 0; i < periods.length; i++) {
    const maxDays =
      periods[i].type === "week" ? 7 : new Date(periods[i].year, periods[i].month, 0).getDate();

    totalDays += maxDays;
    zeroDays += clamp(toNumber(stockoutValues[i]), 0, maxDays);
  }

  const stockoutPct = totalDays > 0 ? (zeroDays / totalDays) * 100 : 0;

  return {
    stock,
    avgPerMonth,
    avgIsForecast,
    coverageMonths,
    proposalQty,
    proposalDate,
    stockoutPct,
    leadTime,
  };
}

// Average forecast sales per month over the visible look-ahead window. The
// forecast values are summed and divided by the number of distinct months they
// span, so it works whether the look-ahead is shown by month or by week.
function forecastMonthlyAverage(forecast) {
  if (!forecast || !forecast.values || !forecast.values.length) {
    return 0;
  }

  const total = forecast.values.reduce((sum, value) => sum + toNumber(value), 0);
  const months = countDistinctAxisMonths(forecast.periods);

  return months > 0 ? total / months : 0;
}

// Zero-stock days for one period. The monthly Dni-0 roll-up is authoritative
// when it is filled, but the workbook leaves it blank on rows whose weekly
// Dni-0 cells are populated. The red shading on the chart is built from those
// weekly columns, so without this fallback the same outage shows as red bands
// under a green "Braki 0%" chip.
function aggregateStockoutDays(matrix, firstCol, selectedProductCount, period) {
  const periodCols = findValueColumns(GROUP_ID.DNI_0, SERIES_ID.DNI_0, period);
  const days = aggregateValueColumns(matrix, periodCols, firstCol, selectedProductCount, "avg");

  if (days > 0 || period.type !== "month") {
    return days;
  }

  return aggregateValueColumns(
    matrix,
    findWeeklyStockoutColumns(period.year, period.month),
    firstCol,
    selectedProductCount,
    "avg"
  );
}

// Weekly Dni-0 columns filed under one month, in calendar order. Sorting by the
// raw week number is wrong at the year boundary: the workbook files the week
// that straddles New Year under both months, so December carries a week 1 and
// January a week 52/53. columnIsoWeekYear puts each on the right side.
function findWeeklyStockoutColumns(year, month) {
  return state.columnMap
    .filter((col) => {
      const m = col.meta;

      return (
        toNumber(m.groupId) === GROUP_ID.DNI_0 &&
        toNumber(m.seriesId) === SERIES_ID.DNI_0 &&
        toNumber(m.levelId) === LEVEL_ID.WEEK &&
        toNumber(m.year) === year &&
        toNumber(m.month) === month
      );
    })
    .sort((a, b) => isoWeekSortKey(a.meta) - isoWeekSortKey(b.meta));
}

function isoWeekSortKey(meta) {
  const week = normalizeWeekOrder(meta.weekOrOrder);

  return columnIsoWeekYear(toNumber(meta.year), toNumber(meta.month), week) * 100 + week;
}

function aggregateColumn(matrix, col, firstCol, selectedProductCount, mode) {
  if (!col) {
    return 0;
  }

  const offset = col.colIndex - firstCol;
  let total = 0;

  for (const row of matrix) {
    total += toNumber(row[offset]);
  }

  if (mode === "avg" && selectedProductCount > 0) {
    return total / selectedProductCount;
  }

  return total;
}

function buildDeliveryMarkers({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  sales,
  deliveryDateValues,
  deliveryDateTexts,
}) {
  const markers = [];
  const deliveryCols = state.columnMap.filter(isDeliveryColumn);

  for (const col of deliveryCols) {
    const qty = aggregateColumn(matrix, col, firstCol, selectedProductCount, state.aggregation);

    if (qty <= 0) {
      continue;
    }

    const offset = col.colIndex - firstCol;
    const rawDate = deliveryDateValues[offset];
    const dateText = deliveryDateTexts[offset];

    const deliveryDate = parseExcelDate(rawDate, dateText);

    if (!deliveryDate) {
      continue;
    }

    const x = dateToPeriodX(deliveryDate, periods);

    if (x === null) {
      continue;
    }

    const y = interpolateSeriesAtX(sales, x);

    markers.push({
      x,
      y,
      qty,
    });
  }

  return markers;
}

function buildLookaheadModel({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  headerValues,
  headerTexts,
}) {
  const stockCol = findCurrentStockColumn();
  const openingStock = aggregateColumn(
    matrix,
    stockCol,
    firstCol,
    selectedProductCount,
    state.aggregation
  );
  const forecastValues = buildLookaheadDemandValues(
    matrix,
    firstCol,
    selectedProductCount,
    periods
  );
  const includeFutureDeliveries = state.aggregation !== "avg";
  const deliveries = includeFutureDeliveries
    ? buildLookaheadDeliveryEvents({
        matrix,
        firstCol,
        selectedProductCount,
        periods,
        headerValues,
        headerTexts,
      })
    : [];
  const proposalEvent = includeFutureDeliveries
    ? buildProposalDeliveryEvent({
        matrix,
        firstCol,
        selectedProductCount,
        periods,
        headerValues,
        headerTexts,
      })
    : null;
  const lookaheadDeliveries = proposalEvent ? [...deliveries, proposalEvent] : deliveries;
  const { points, markers } = buildLookaheadPoints(
    openingStock,
    forecastValues,
    lookaheadDeliveries.sort((a, b) => a.x - b.x),
    periods
  );

  return {
    periods,
    points,
    markers,
  };
}

function findCurrentStockColumn() {
  return findColumnByUid(COLUMN_UID.CURRENT_STOCK_6) || null;
}

// Demand the look-ahead consumes against. computeKpis already falls back to the
// historical average when a product has no forecast at all; without the same
// fallback here the stock line stays flat ("never depletes") while the chip
// above it shows a finite coverage from that very average.
function buildLookaheadDemandValues(matrix, firstCol, selectedProductCount, periods) {
  if (!state.useForecastSource) {
    return buildAverageSourceValues(matrix, firstCol, selectedProductCount, periods);
  }

  const forecast = buildForecastSourceValues(matrix, firstCol, selectedProductCount, periods);
  const total = forecast.reduce((sum, value) => sum + toNumber(value), 0);

  return total > 0
    ? forecast
    : buildAverageSourceValues(matrix, firstCol, selectedProductCount, periods);
}

function buildForecastSourceValues(matrix, firstCol, selectedProductCount, periods) {
  return periods.map((period) => {
    const forecastCols = findValueColumns(GROUP_ID.PROGNOZA, SERIES_ID.PROGNOZA, period);

    if (forecastCols.length) {
      return aggregateValueColumns(
        matrix,
        forecastCols,
        firstCol,
        selectedProductCount,
        state.aggregation
      );
    }

    if (!period.synthetic) {
      return 0;
    }

    // A synthetic week stands in for a month with no weekly prognoza columns,
    // so no week-level column can ever match it. Split that month's forecast
    // evenly across the weeks that stand in for it, otherwise switching the
    // look-ahead to "W" silently zeroes a forecast the workbook does carry.
    return splitMonthForecastAcrossWeeks(matrix, firstCol, selectedProductCount, periods, period);
  });
}

function splitMonthForecastAcrossWeeks(matrix, firstCol, selectedProductCount, periods, period) {
  const monthCols = findValueColumns(GROUP_ID.PROGNOZA, SERIES_ID.PROGNOZA, {
    type: "month",
    year: period.year,
    month: period.month,
  });
  const monthTotal = aggregateValueColumns(
    matrix,
    monthCols,
    firstCol,
    selectedProductCount,
    state.aggregation
  );
  const weekCount = periods.filter(
    (candidate) =>
      candidate.synthetic && candidate.year === period.year && candidate.month === period.month
  ).length;

  return weekCount > 0 ? monthTotal / weekCount : monthTotal;
}

function buildForecastChartModel({ matrix, firstCol, selectedProductCount, periods }) {
  return {
    periods,
    values: buildForecastSourceValues(matrix, firstCol, selectedProductCount, periods),
  };
}

function buildAverageSourceValues(matrix, firstCol, selectedProductCount, periods) {
  const averageCol = findColumnByUid(COLUMN_UID.AVERAGE);
  const correctionCol = findColumnByUid(COLUMN_UID.AVERAGE_CORRECTION);
  const monthlyAverage = aggregateAverageFallbackColumn(
    matrix,
    correctionCol,
    averageCol,
    firstCol,
    selectedProductCount,
    state.aggregation
  );

  return periods.map((period) => {
    if (period.type !== "week") {
      return monthlyAverage;
    }

    const weeksInMonth = periods.filter(
      (candidate) => candidate.year === period.year && candidate.month === period.month
    ).length;

    return weeksInMonth > 0 ? monthlyAverage / weeksInMonth : monthlyAverage;
  });
}

function aggregateAverageFallbackColumn(
  matrix,
  correctionCol,
  averageCol,
  firstCol,
  selectedProductCount,
  mode
) {
  const correctionOffset = correctionCol ? correctionCol.colIndex - firstCol : null;
  const averageOffset = averageCol ? averageCol.colIndex - firstCol : null;
  let total = 0;

  for (const row of matrix) {
    const correction = getOptionalNumber(correctionOffset === null ? null : row[correctionOffset]);
    const average = getOptionalNumber(averageOffset === null ? null : row[averageOffset]);

    total += correction ?? average ?? 0;
  }

  if (mode === "avg" && selectedProductCount > 0) {
    return total / selectedProductCount;
  }

  return total;
}

function buildLookaheadDeliveryEvents({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  headerValues,
  headerTexts,
}) {
  const events = [];
  const deliveryCols = state.columnMap.filter(isFutureWarehouseOrderColumn);

  for (const col of deliveryCols) {
    const qty = aggregateColumn(matrix, col, firstCol, selectedProductCount, state.aggregation);

    if (qty <= 0) {
      continue;
    }

    const offset = col.colIndex - firstCol;
    const deliveryDate = getDeliveryDateFromHeaders(headerValues, headerTexts, offset, periods);

    if (!deliveryDate) {
      continue;
    }

    const x = dateToLookaheadX(deliveryDate, periods);

    if (x === null) {
      continue;
    }

    events.push({
      x,
      qty,
    });
  }

  return events.sort((a, b) => a.x - b.x);
}

function getDeliveryDateFromHeaders(headerValues, headerTexts, offset, periods) {
  const rowIndexes = [CONFIG.deliveryDateRowIndex, 2, 1];
  const seenRows = new Set();

  for (const rowIndex of rowIndexes) {
    if (seenRows.has(rowIndex)) {
      continue;
    }

    seenRows.add(rowIndex);

    const rawValue = getHeaderRow(headerValues, rowIndex)[offset];
    const textValue = getHeaderRow(headerTexts, rowIndex)[offset];
    const date = parseExcelDate(rawValue, textValue);

    if (date && dateToLookaheadX(date, periods) !== null) {
      return date;
    }
  }

  return null;
}

function buildProposalDeliveryEvent({
  matrix,
  firstCol,
  selectedProductCount,
  periods,
  headerValues,
}) {
  const leadTimeMonths = getLeadTimeMonths(headerValues, firstCol);
  const proposalCol = findColumnByUid(COLUMN_UID.PROPOSAL);
  const qty = aggregateColumn(
    matrix,
    proposalCol,
    firstCol,
    selectedProductCount,
    state.aggregation
  );

  if (!proposalCol || qty <= 0 || leadTimeMonths <= 0) {
    return null;
  }

  const proposalDate = addMonths(new Date(), leadTimeMonths);
  const x = dateToLookaheadX(proposalDate, periods);

  if (x === null) {
    return null;
  }

  return {
    x,
    qty,
    kind: "proposal",
  };
}

// Lead time is a workbook-global value stored in the header band of the
// C0026 column (the "Lead time: N" label/value pair). It is the same for every
// product, so we read it from the header rows rather than per-product data.
// Guard with a sane range so a stray header number can't produce a bogus
// proposal date decades away.
function getLeadTimeMonths(headerValues, firstCol) {
  const leadTimeCol = findColumnByUid(COLUMN_UID.LEAD_TIME_MONTHS);

  if (!leadTimeCol) {
    return 0;
  }

  const offset = leadTimeCol.colIndex - firstCol;

  if (offset < 0) {
    return 0;
  }

  for (const row of headerValues || []) {
    if (!row || offset >= row.length) {
      continue;
    }

    const months = getOptionalNumber(row[offset]);

    if (months !== null && months > 0 && months <= MAX_LEAD_TIME_MONTHS) {
      return months;
    }
  }

  return 0;
}

// The opening stock is what the warehouse holds today, but x = 0 is the START
// of the first period — and since the prognoza block begins at the current
// month, that is the 1st. Consuming the whole month's forecast from a stock
// figure that already reflects the elapsed part of it double-counts those days
// and pulls the projected stock-out up to a month early. Hold the balance flat
// until today's position on the axis (the same calendar fraction deliveries are
// placed with) and only consume what is still ahead.
function buildLookaheadPoints(openingStock, forecastValues, deliveries, periods) {
  const startX = getLookaheadStartX(periods);
  const points = [{ x: 0, y: Math.max(0, openingStock) }];
  const markers = [];
  let balance = Math.max(0, openingStock);

  if (startX > 0) {
    pushLookaheadPoint(points, startX, balance);
  }

  for (let i = 0; i < periods.length; i++) {
    const periodForecast = toNumber(forecastValues[i]);
    const periodStart = Math.max(i, startX);

    if (periodStart >= i + 1) {
      continue; // period already elapsed
    }

    const periodDeliveries = deliveries.filter(
      (delivery) => delivery.x >= periodStart && delivery.x < i + 1
    );
    let cursor = periodStart;

    for (const delivery of periodDeliveries) {
      balance = consumeLookaheadStock(points, balance, periodForecast, cursor, delivery.x);

      balance += delivery.qty;
      points.push({ x: delivery.x, y: balance });

      markers.push({
        x: delivery.x,
        y: balance,
        qty: delivery.qty,
        kind: delivery.kind,
      });

      cursor = delivery.x;
    }

    balance = consumeLookaheadStock(points, balance, periodForecast, cursor, i + 1);
  }

  return {
    points,
    markers,
  };
}

// Today's position on the look-ahead axis, or 0 when the window starts in the
// future (nothing of the first period has elapsed yet).
function getLookaheadStartX(periods) {
  const x = dateToLookaheadX(new Date(), periods);

  return Number.isFinite(x) ? clamp(x, 0, periods.length) : 0;
}

function consumeLookaheadStock(points, balance, periodForecast, fromX, toX) {
  const duration = Math.max(0, toX - fromX);
  const forecastRate = Math.max(0, periodForecast);

  if (duration <= 0) {
    return Math.max(0, balance);
  }

  if (forecastRate <= 0 || balance <= 0) {
    pushLookaheadPoint(points, toX, Math.max(0, balance));
    return Math.max(0, balance);
  }

  const consumed = forecastRate * duration;

  if (consumed < balance) {
    const nextBalance = balance - consumed;
    pushLookaheadPoint(points, toX, nextBalance);
    return nextBalance;
  }

  const zeroX = fromX + balance / forecastRate;
  pushLookaheadPoint(points, zeroX, 0);
  pushLookaheadPoint(points, toX, 0);

  return 0;
}

function pushLookaheadPoint(points, x, y) {
  const clampedY = Math.max(0, y);
  const last = points[points.length - 1];

  if (last && Math.abs(last.x - x) < 0.0001) {
    last.y = clampedY;
    return;
  }

  points.push({
    x,
    y: clampedY,
  });
}

function buildWeeklyStockoutIntervals(periods, stockoutValues) {
  const intervalsBySegment = Array.from({ length: Math.max(periods.length - 1, 0) }, () => []);

  for (let i = 0; i < periods.length; i++) {
    const zeroDays = clamp(toNumber(stockoutValues[i]), 0, 7);
    const ratio = clamp01(zeroDays / 7);

    if (ratio <= 0) {
      continue;
    }

    if (i < periods.length - 1) {
      intervalsBySegment[i].push(getDirectionalInterval(ratio, i, stockoutValues));
    } else if (i > 0) {
      intervalsBySegment[i - 1].push({ from: 1 - ratio, to: 1 });
    }
  }

  return intervalsBySegment;
}

function buildMonthlyStockoutIntervals(
  periods,
  matrix,
  firstCol,
  selectedProductCount,
  monthlyD0Values
) {
  const intervalsBySegment = Array.from({ length: Math.max(periods.length - 1, 0) }, () => []);

  for (let i = 0; i < periods.length; i++) {
    const period = periods[i];

    const weeklyIntervals = buildWeeklyIntervalsInsideMonth(
      period.year,
      period.month,
      matrix,
      firstCol,
      selectedProductCount
    );

    if (weeklyIntervals.length > 0) {
      if (i < periods.length - 1) {
        intervalsBySegment[i].push(...weeklyIntervals);
      } else if (i > 0) {
        intervalsBySegment[i - 1].push(...shiftIntervalsToRightEdge(weeklyIntervals));
      }

      continue;
    }

    const daysInMonth = new Date(period.year, period.month, 0).getDate();
    const zeroDays = clamp(toNumber(monthlyD0Values[i]), 0, daysInMonth);
    const ratio = clamp01(zeroDays / daysInMonth);

    if (ratio <= 0) {
      continue;
    }

    if (i < periods.length - 1) {
      intervalsBySegment[i].push(getDirectionalInterval(ratio, i, monthlyD0Values));
    } else if (i > 0) {
      intervalsBySegment[i - 1].push({ from: 1 - ratio, to: 1 });
    }
  }

  return intervalsBySegment;
}

function buildWeeklyIntervalsInsideMonth(year, month, matrix, firstCol, selectedProductCount) {
  const weeklyD0Cols = findWeeklyStockoutColumns(year, month);

  if (!weeklyD0Cols.length) {
    return [];
  }

  const intervals = [];
  const bucketCount = weeklyD0Cols.length;

  for (let i = 0; i < weeklyD0Cols.length; i++) {
    const col = weeklyD0Cols[i];
    const zeroDays = aggregateColumn(matrix, col, firstCol, selectedProductCount, "avg");
    const ratioInsideWeek = clamp01(clamp(zeroDays, 0, 7) / 7);

    if (ratioInsideWeek <= 0) {
      continue;
    }

    const bucketStart = i / bucketCount;
    const bucketWidth = 1 / bucketCount;

    const prevZero =
      i > 0 &&
      aggregateColumn(matrix, weeklyD0Cols[i - 1], firstCol, selectedProductCount, "avg") > 0;
    const nextZero =
      i < weeklyD0Cols.length - 1 &&
      aggregateColumn(matrix, weeklyD0Cols[i + 1], firstCol, selectedProductCount, "avg") > 0;

    let from;
    let to;

    if (!prevZero && nextZero) {
      from = bucketStart + bucketWidth * (1 - ratioInsideWeek);
      to = bucketStart + bucketWidth;
    } else {
      from = bucketStart;
      to = bucketStart + bucketWidth * ratioInsideWeek;
    }

    intervals.push({ from, to });
  }

  return mergeIntervals(intervals);
}

function getDirectionalInterval(ratio, index, values) {
  const current = toNumber(values[index]);
  const prev = index > 0 ? toNumber(values[index - 1]) : 0;
  const next = index < values.length - 1 ? toNumber(values[index + 1]) : 0;

  if (current <= 0 || ratio <= 0) {
    return { from: 0, to: 0 };
  }

  // The red band width is always proportional to the share of zero-stock days
  // (ratio). Only its position depends on which neighbour was also out of
  // stock, so a continuous outage reads as continuous. Never fill the whole
  // segment unless the period really was out the whole time (ratio ~ 1).
  if (prev <= 0 && next > 0) {
    // Outage continues into the next period -> dock the band to the right edge.
    return { from: 1 - ratio, to: 1 };
  }

  if (prev > 0 && next <= 0) {
    // Outage started in the previous period -> dock the band to the left edge.
    return { from: 0, to: ratio };
  }

  // Isolated outage, or out on both sides: centre a band of width `ratio`.
  const pad = (1 - ratio) / 2;
  return { from: pad, to: pad + ratio };
}

// The last period has no segment of its own to shade, so its outage is docked
// to the right edge of the previous segment. Mapping each interval separately
// would land them all on the same band, and mergeIntervals would then fold them
// into one — silently dropping most of the month's outage. Collapse them into a
// single band of the same total width instead, which is what the monthly path
// already does with its ratio.
function shiftIntervalsToRightEdge(intervals) {
  const width = mergeIntervals(intervals).reduce((total, x) => total + (x.to - x.from), 0);

  if (width <= 0) {
    return [];
  }

  return [{ from: clamp01(1 - width), to: 1 }];
}

function mergeIntervals(intervals) {
  if (!intervals.length) {
    return [];
  }

  const sorted = intervals
    .map((x) => ({ from: clamp01(x.from), to: clamp01(x.to) }))
    .filter((x) => x.to > x.from)
    .sort((a, b) => a.from - b.from);

  const result = [];

  for (const current of sorted) {
    const last = result[result.length - 1];

    if (!last || current.from > last.to) {
      result.push(current);
    } else {
      last.to = Math.max(last.to, current.to);
    }
  }

  return result;
}

function getDeliveryMarkerTopPadding(markers, basePadding) {
  if (!markers?.length) {
    return basePadding;
  }

  return markers.length > 1 ? 40 : 26;
}

function renderModel(model) {
  state.lastModel = model;
  hideAllChartHovers();
  document.getElementById("productTitle").textContent = model.title || "Towar";
  renderKpiStrip(model.kpis);
  renderSalesChart(model);
  renderLookaheadChart(model.lookahead);
  renderForecastChart(model.forecast);
  setStatus(
    `Updated. ${state.monthWindow}M / ${state.detail === "month" ? "M" : "W"} / ${aggregationLabel()}.`
  );
}

function renderSalesChart(model) {
  const canvas = document.getElementById("salesChart");
  const palette = getThemePalette();
  const deliveryMarkers = state.showDeliveries ? model.deliveries : [];

  destroySalesChart();

  const existing = ChartRuntime.getChart(canvas);
  if (existing) {
    existing.destroy();
  }

  const datasets = [
    buildScaleDataset("Sprzedaż", model.sales),
    buildScaleDataset("Sprzedaż markety", model.market),
  ];

  state.salesChart = new ChartRuntime(canvas, {
    type: "line",
    data: {
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      animation: false,
      layout: {
        padding: {
          top: getDeliveryMarkerTopPadding(deliveryMarkers, 8),
          right: 6,
          bottom: 0,
        },
      },
      interaction: {
        mode: "nearest",
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        salesLine: {
          series: [
            {
              values: model.sales,
              intervalsBySegment: model.stockoutIntervals,
              normalColor: "rgb(37, 99, 235)",
              stockoutColor: "rgb(239, 68, 68)",
              width: 3,
              tension: 0.32,
            },
            {
              values: model.market,
              intervalsBySegment: model.stockoutIntervals,
              normalColor: "rgb(22, 163, 74)",
              stockoutColor: "rgb(239, 68, 68)",
              width: 2.4,
              tension: 0.32,
            },
          ],
        },
        deliveryMarker: {
          dockTop: true,
          markers: deliveryMarkers,
        },
      },
      scales: {
        x: {
          type: "linear",
          min: 0,
          max: Math.max(model.labels.length - 1, 1),
          ticks: {
            stepSize: 1,
            maxRotation: 0,
            autoSkip: false,
            color: palette.axis,
            labelGap: 1,
            padding: 3,
            font: {
              size: 8,
            },
            callback: (value) => {
              const index = Math.round(value);

              if (Math.abs(value - index) > 0.001) {
                return "";
              }

              return getLookaheadAxisLabel(model.periods, index);
            },
          },
          grid: {
            display: true,
            color: palette.xGrid,
            tickLength: 0,
          },
          border: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          border: {
            display: false,
          },
          grid: {
            color: palette.yGrid,
            tickLength: 0,
          },
          ticks: {
            callback: (value) => formatNumber(value),
            color: palette.axis,
            maxTicksLimit: 5,
            padding: 5,
            font: {
              size: 9,
            },
          },
        },
      },
    },
  });
}

function renderLookaheadChart(model) {
  const canvas = document.getElementById("lookaheadChart");
  const palette = getThemePalette();
  const deliveryMarkers = state.showDeliveries ? model.markers : [];

  destroyLookaheadChart();

  const existing = ChartRuntime.getChart(canvas);
  if (existing) {
    existing.destroy();
  }

  state.lookaheadChart = new ChartRuntime(canvas, {
    type: "line",
    data: {
      datasets: [
        {
          data: model.points,
          borderColor: "rgb(79, 70, 229)",
          backgroundColor: "rgba(79, 70, 229, 0.08)",
          borderWidth: 2.6,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          fill: true,
          tension: 0,
          stepped: false,
          segment: {
            borderColor: (ctx) => (ctx.p1.parsed.y <= 0 ? "rgb(239, 68, 68)" : "rgb(79, 70, 229)"),
          },
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      animation: false,
      layout: {
        padding: {
          top: getDeliveryMarkerTopPadding(deliveryMarkers, 8),
          right: 5,
          left: 2,
          bottom: 2,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        salesLine: {
          series: [],
        },
        monthGrid: {
          positions: buildLookaheadMonthGridPositions(model.periods),
        },
        deliveryMarker: {
          dockTop: true,
          markers: deliveryMarkers,
        },
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          min: 0,
          max: Math.max(model.periods.length, 1),
          ticks: {
            display: true,
            stepSize: 1,
            maxRotation: 0,
            autoSkip: false,
            color: palette.axis,
            labelGap: 1,
            padding: 4,
            font: {
              size: 8,
            },
            callback: (value) => {
              const index = Math.round(value);

              if (Math.abs(value - index) > 0.001 || index >= model.periods.length) {
                return "";
              }

              return getLookaheadAxisLabel(model.periods, index);
            },
          },
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          min: 0,
          ticks: {
            display: true,
            callback: (value) => formatNumber(value),
            color: palette.axis,
            maxTicksLimit: 4,
            padding: 4,
            font: {
              size: 9,
            },
          },
          grid: {
            color: palette.yGrid,
            tickLength: 0,
          },
          border: {
            display: false,
          },
        },
      },
    },
  });
}

function renderForecastChart(model) {
  const canvas = document.getElementById("forecastChart");
  const palette = getThemePalette();

  destroyForecastChart();

  const existing = ChartRuntime.getChart(canvas);
  if (existing) {
    existing.destroy();
  }

  state.forecastChart = new ChartRuntime(canvas, {
    type: "line",
    data: {
      datasets: [
        {
          data: model.values.map((y, i) => ({ x: i, y })),
          borderColor: "rgb(14, 116, 144)",
          backgroundColor: "rgba(14, 116, 144, 0.08)",
          borderWidth: 2.4,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          fill: true,
          tension: 0.28,
          stepped: false,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      animation: false,
      layout: {
        padding: {
          top: 8,
          right: 5,
          left: 2,
          bottom: 2,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        salesLine: {
          series: [],
        },
        monthGrid: {
          positions: buildLookaheadMonthGridPositions(model.periods),
        },
        deliveryMarker: {
          markers: [],
        },
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          min: 0,
          max: Math.max(model.periods.length - 1, 1),
          ticks: {
            display: true,
            stepSize: 1,
            maxRotation: 0,
            autoSkip: false,
            color: palette.axis,
            labelGap: 1,
            padding: 4,
            font: {
              size: 8,
            },
            callback: (value) => {
              const index = Math.round(value);

              if (Math.abs(value - index) > 0.001 || index >= model.periods.length) {
                return "";
              }

              return getLookaheadAxisLabel(model.periods, index);
            },
          },
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          min: 0,
          ticks: {
            display: true,
            callback: (value) => formatNumber(value),
            color: palette.axis,
            maxTicksLimit: 4,
            padding: 4,
            font: {
              size: 9,
            },
          },
          grid: {
            color: palette.yGrid,
            tickLength: 0,
          },
          border: {
            display: false,
          },
        },
      },
    },
  });
}

function buildLookaheadMonthGridPositions(periods) {
  const positions = [];

  for (let i = 0; i < periods.length; i++) {
    const current = periods[i];
    const previous = periods[i - 1];

    if (!previous || previous.year !== current.year || previous.month !== current.month) {
      positions.push(i);
    }
  }

  return positions;
}

function getLookaheadAxisLabel(periods, index) {
  const period = periods[index];

  if (!period) {
    return "";
  }

  if (period.type === "month") {
    return getCompactMonthAxisLabel(periods, index);
  }

  const previous = periods[index - 1];

  if (previous && previous.year === period.year && previous.month === period.month) {
    return "";
  }

  return getCompactMonthAxisLabel(periods, index);
}

function getCompactMonthAxisLabel(periods, index) {
  const period = periods[index];

  if (!period) {
    return "";
  }

  const month = String(period.month).padStart(2, "0");
  const year = String(period.year).slice(-2);
  const monthCount = countDistinctAxisMonths(periods);
  const previousMonth = findPreviousAxisMonth(periods, index);

  if (monthCount < 8) {
    return `${month}.${year}`;
  }

  if (!previousMonth || previousMonth.year !== period.year || period.month === 1) {
    return `${month}.${year}`;
  }

  return month;
}

function countDistinctAxisMonths(periods) {
  const keys = new Set();

  for (const period of periods || []) {
    if (Number.isInteger(period?.year) && Number.isInteger(period?.month)) {
      keys.add(monthKey(period.year, period.month));
    }
  }

  return keys.size;
}

function findPreviousAxisMonth(periods, index) {
  const period = periods[index];

  if (!period) {
    return null;
  }

  for (let i = index - 1; i >= 0; i--) {
    const candidate = periods[i];

    if (!candidate) {
      continue;
    }

    if (candidate.year !== period.year || candidate.month !== period.month) {
      return candidate;
    }
  }

  return null;
}

function buildScaleDataset(label, values) {
  return {
    label,
    data: values.map((y, i) => ({ x: i, y })),
    showLine: false,
    pointRadius: 0,
    pointHoverRadius: 0,
    pointHitRadius: 0,
    borderWidth: 0,
    borderColor: "rgba(0, 0, 0, 0)",
    backgroundColor: "rgba(0, 0, 0, 0)",
  };
}

function drawSalesLine(ctx, xScale, yScale, series) {
  const values = series.values || [];

  if (values.length < 2) {
    return;
  }

  ctx.lineWidth = series.width || 2.6;
  ctx.lineCap = "butt";
  ctx.lineJoin = "round";
  ctx.setLineDash(series.dash || []);

  const curve = buildCurveSegments(values, series.tension || 0);

  for (let i = 0; i < values.length - 1; i++) {
    const redIntervals = mergeIntervals(series.intervalsBySegment?.[i] || []);
    const normalIntervals = complementIntervals(redIntervals);
    const pieces = [
      ...normalIntervals.map((interval) => ({
        ...interval,
        color: series.normalColor,
      })),
      ...redIntervals.map((interval) => ({
        ...interval,
        color: series.stockoutColor,
      })),
    ].sort((a, b) => a.from - b.from);

    for (const piece of pieces) {
      drawCurveInterval(ctx, xScale, yScale, curve[i], piece.from, piece.to, piece.color);
    }
  }
}

function buildCurveSegments(values, tension) {
  const segments = [];

  for (let i = 0; i < values.length - 1; i++) {
    const p0 = {
      x: Math.max(0, i - 1),
      y: values[Math.max(0, i - 1)],
    };
    const p1 = {
      x: i,
      y: values[i],
    };
    const p2 = {
      x: i + 1,
      y: values[i + 1],
    };
    const p3 = {
      x: Math.min(values.length - 1, i + 2),
      y: values[Math.min(values.length - 1, i + 2)],
    };
    const amount = tension / 6;

    segments.push({
      p1,
      c1: {
        x: p1.x + (p2.x - p0.x) * amount,
        y: p1.y + (p2.y - p0.y) * amount,
      },
      c2: {
        x: p2.x - (p3.x - p1.x) * amount,
        y: p2.y - (p3.y - p1.y) * amount,
      },
      p2,
    });
  }

  return segments;
}

function drawCurveInterval(ctx, xScale, yScale, segment, from, to, color) {
  const seamOverlap = 0.0015;
  const safeFrom = clamp01(from - seamOverlap);
  const safeTo = clamp01(to + seamOverlap);

  if (safeTo <= safeFrom) {
    return;
  }

  const subcurve = cubicSubcurve(segment, safeFrom, safeTo);
  const start = subcurve.p1;
  const c1 = subcurve.c1;
  const c2 = subcurve.c2;
  const end = subcurve.p2;

  const x1 = xScale.getPixelForValue(start.x);
  const yPx1 = yScale.getPixelForValue(start.y);
  const c1x = xScale.getPixelForValue(c1.x);
  const c1y = yScale.getPixelForValue(c1.y);
  const c2x = xScale.getPixelForValue(c2.x);
  const c2y = yScale.getPixelForValue(c2.y);
  const x2 = xScale.getPixelForValue(end.x);
  const yPx2 = yScale.getPixelForValue(end.y);

  if (![x1, yPx1, c1x, c1y, c2x, c2y, x2, yPx2].every(Number.isFinite)) {
    return;
  }

  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1, yPx1);
  ctx.bezierCurveTo(c1x, c1y, c2x, c2y, x2, yPx2);
  ctx.stroke();
}

function cubicSubcurve(segment, from, to) {
  const left = splitCubic(segment, to).left;

  if (from <= 0) {
    return left;
  }

  return splitCubic(left, from / to).right;
}

function splitCubic(segment, t) {
  const p01 = lerpPoint(segment.p1, segment.c1, t);
  const p12 = lerpPoint(segment.c1, segment.c2, t);
  const p23 = lerpPoint(segment.c2, segment.p2, t);
  const p012 = lerpPoint(p01, p12, t);
  const p123 = lerpPoint(p12, p23, t);
  const p0123 = lerpPoint(p012, p123, t);

  return {
    left: {
      p1: segment.p1,
      c1: p01,
      c2: p012,
      p2: p0123,
    },
    right: {
      p1: p0123,
      c1: p123,
      c2: p23,
      p2: segment.p2,
    },
  };
}

function lerpPoint(a, b, t) {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
  };
}

function complementIntervals(intervals) {
  const result = [];
  let cursor = 0;

  const clean = mergeIntervals(intervals);

  for (const interval of clean) {
    if (interval.from > cursor) {
      result.push({
        from: cursor,
        to: interval.from,
      });
    }

    cursor = Math.max(cursor, interval.to);
  }

  if (cursor < 1) {
    result.push({
      from: cursor,
      to: 1,
    });
  }

  return result;
}

function renderEmpty(message) {
  state.lastModel = null;
  hideAllChartHovers();
  document.getElementById("productTitle").textContent = message;
  renderKpiStrip(null);
  destroySalesChart();
  destroyLookaheadChart();
  destroyForecastChart();
  setStatus(message);
}

function renderKpiStrip(kpis) {
  const el = document.getElementById("kpiStrip");

  if (!el) {
    return;
  }

  if (!kpis) {
    el.innerHTML = "";
    return;
  }

  const chips = [];

  chips.push(kpiChip("Stok", formatNumber(kpis.stock)));
  // The label stays "Śr./mc" regardless of the Prog. button — only the
  // underlying value switches between historical and forecast (see computeKpis).
  chips.push(kpiChip("Śr./mc", formatNumber(kpis.avgPerMonth)));

  if (Number.isFinite(kpis.coverageMonths)) {
    const floor = Math.max(kpis.leadTime, 1);
    const tone =
      kpis.coverageMonths < floor ? "danger" : kpis.coverageMonths < floor + 2 ? "warn" : "ok";

    chips.push(kpiChip("Pokrycie", `${kpis.coverageMonths.toFixed(1)} mc`, tone));
  }

  if (kpis.proposalQty > 0) {
    const date = kpis.proposalDate;
    const when = date
      ? ` · ${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getFullYear()).slice(-2)}`
      : "";

    chips.push(kpiChip("Propozycja", `${formatNumber(kpis.proposalQty)}${when}`, "accent"));
  }

  const stockoutTone = kpis.stockoutPct >= 15 ? "danger" : kpis.stockoutPct >= 5 ? "warn" : "ok";
  chips.push(kpiChip("Braki", `${Math.round(kpis.stockoutPct)}%`, stockoutTone));

  el.innerHTML = chips.join("");
}

function kpiChip(label, value, tone) {
  const cls = tone ? ` kpi-${tone}` : "";

  return `<span class="kpi${cls}">${label} <b>${value}</b></span>`;
}

// Hover read-out for the canvas charts. MiniChart has no built-in interaction,
// so each chart wrapper gets HTML overlay elements (a vertical cursor, marker
// dots on the lines, and a value tooltip) positioned from the live chart scales
// on mousemove. Listeners are attached once; they read the current model.
const chartHoverOverlays = [];

function wireChartHover() {
  if (state.hoverWired) {
    return;
  }

  // Latch only once the listeners are actually attached. Setting it first meant
  // a single early call — before the canvases are in the DOM — killed hover for
  // the rest of the session.
  const wired = [
    setupChartHover("salesChart", buildSalesHover),
    setupChartHover("lookaheadChart", buildLookaheadHover),
    setupChartHover("forecastChart", buildForecastHover),
  ];

  state.hoverWired = wired.every(Boolean);
}

// Overlays survive chart rebuilds, so a re-render has to clear them: they are
// only hidden on mouseleave, and a selection change while the pointer rests on
// a canvas would otherwise leave the previous product's tooltip and dots
// painted over the new chart — or over three empty cards after renderEmpty.
function hideAllChartHovers() {
  for (const overlay of chartHoverOverlays) {
    hideChartHover(overlay);
  }
}

function setupChartHover(canvasId, builder) {
  const canvas = document.getElementById(canvasId);

  if (!canvas || !canvas.parentElement) {
    return false;
  }

  const wrap = canvas.parentElement;
  const cursor = document.createElement("div");
  cursor.className = "chart-cursor";

  const tip = document.createElement("div");
  tip.className = "chart-tip";

  const dots = [createHoverDot(wrap), createHoverDot(wrap)];

  wrap.appendChild(cursor);
  wrap.appendChild(tip);

  const overlay = { cursor, tip, dots };

  chartHoverOverlays.push(overlay);

  canvas.addEventListener("mousemove", (event) => {
    handleChartHover(event, canvas, builder, overlay);
  });
  canvas.addEventListener("mouseleave", () => hideChartHover(overlay));

  return true;
}

function createHoverDot(wrap) {
  const dot = document.createElement("div");
  dot.className = "chart-dot";
  wrap.appendChild(dot);

  return dot;
}

function hideChartHover(overlay) {
  overlay.cursor.style.display = "none";
  overlay.tip.style.display = "none";
  overlay.dots.forEach((dot) => {
    dot.style.display = "none";
  });
}

function handleChartHover(event, canvas, builder, overlay) {
  const chart = ChartRuntime.getChart(canvas);
  const model = state.lastModel;

  if (!chart || !chart.scales || !chart.scales.x || !model) {
    hideChartHover(overlay);
    return;
  }

  const xScale = chart.scales.x;
  const pixelMin = xScale.getPixelForValue(xScale.min);
  const pixelMax = xScale.getPixelForValue(xScale.max);

  if (!Number.isFinite(pixelMin) || !Number.isFinite(pixelMax) || pixelMax === pixelMin) {
    hideChartHover(overlay);
    return;
  }

  const ratio = (event.offsetX - pixelMin) / (pixelMax - pixelMin);
  const xValue = xScale.min + ratio * (xScale.max - xScale.min);
  const info = builder(model, xValue, chart);

  if (!info) {
    hideChartHover(overlay);
    return;
  }

  const wrapWidth = canvas.parentElement.clientWidth;

  overlay.cursor.style.left = `${info.px}px`;
  overlay.cursor.style.display = "block";

  let topDot = Infinity;

  overlay.dots.forEach((dot, index) => {
    const point = info.dots[index];

    if (point && Number.isFinite(point.y)) {
      dot.style.left = `${info.px}px`;
      dot.style.top = `${point.y}px`;
      dot.style.background = point.color;
      dot.style.display = "block";
      topDot = Math.min(topDot, point.y);
    } else {
      dot.style.display = "none";
    }
  });

  overlay.tip.innerHTML = info.html;
  overlay.tip.style.display = "block";

  const tipWidth = overlay.tip.offsetWidth;
  const tipHeight = overlay.tip.offsetHeight;
  const minLeft = tipWidth / 2 + 2;
  const left = clamp(info.px, minLeft, Math.max(minLeft, wrapWidth - tipWidth / 2 - 2));
  const top = Number.isFinite(topDot) ? Math.max(2, topDot - tipHeight - 6) : 2;

  overlay.tip.style.left = `${left}px`;
  overlay.tip.style.top = `${top}px`;
}

function buildSalesHover(model, xValue, chart) {
  const count = model.labels.length;

  if (!count) {
    return null;
  }

  const index = clamp(Math.round(xValue), 0, count - 1);
  const px = chart.scales.x.getPixelForValue(index);
  const yScale = chart.scales.y;

  return {
    px,
    dots: [
      { y: yScale.getPixelForValue(model.sales[index]), color: "#2563eb" },
      { y: yScale.getPixelForValue(model.market[index]), color: "#16a34a" },
    ],
    // periodFullLabel, not the axis label: in week detail the axis says "W18",
    // which is ambiguous across a 20-month window. The other two hovers already
    // spell out the month.
    html: buildHoverHtml(periodFullLabel(model.periods[index]), [
      { color: "#2563eb", label: "Sprzedaż", value: model.sales[index] },
      { color: "#16a34a", label: "Markety", value: model.market[index] },
    ]),
  };
}

function buildForecastHover(model, xValue, chart) {
  const forecast = model.forecast;
  const count = forecast.values.length;

  if (!count) {
    return null;
  }

  const index = clamp(Math.round(xValue), 0, count - 1);
  const px = chart.scales.x.getPixelForValue(index);

  return {
    px,
    dots: [{ y: chart.scales.y.getPixelForValue(forecast.values[index]), color: "#0e7490" }],
    html: buildHoverHtml(periodFullLabel(forecast.periods[index]), [
      { color: "#0e7490", label: "Prognoza", value: forecast.values[index] },
    ]),
  };
}

function buildLookaheadHover(model, xValue, chart) {
  const lookahead = model.lookahead;
  const count = lookahead.periods.length;

  if (!count) {
    return null;
  }

  const index = clamp(Math.round(xValue), 0, count - 1);
  const px = chart.scales.x.getPixelForValue(index);
  const stock = interpolatePointsAtX(lookahead.points, index);

  return {
    px,
    dots: [{ y: chart.scales.y.getPixelForValue(stock), color: "#4f46e5" }],
    html: buildHoverHtml(periodFullLabel(lookahead.periods[index]), [
      { color: "#4f46e5", label: "Stan", value: stock },
    ]),
  };
}

function buildHoverHtml(title, rows) {
  const body = rows
    .map(
      (row) =>
        `<div><span class="tip-dot" style="background:${row.color}"></span>${row.label} <b>${formatNumber(
          row.value
        )}</b></div>`
    )
    .join("");

  return `<div class="tip-title">${escapeHoverText(title)}</div>${body}`;
}

function escapeHoverText(value) {
  return String(value == null ? "" : value).replace(/[&<>]/g, (ch) => {
    if (ch === "&") return "&amp;";
    if (ch === "<") return "&lt;";
    return "&gt;";
  });
}

function periodFullLabel(period) {
  if (!period) {
    return "";
  }

  const month = String(period.month).padStart(2, "0");
  const year = String(period.year).slice(-2);

  if (period.type === "week") {
    return `W${period.weekOrder} · ${month}.${year}`;
  }

  return `${month}.${year}`;
}

function interpolatePointsAtX(points, x) {
  if (!points || !points.length) {
    return 0;
  }

  if (x <= points[0].x) {
    return points[0].y;
  }

  const last = points[points.length - 1];

  if (x >= last.x) {
    return last.y;
  }

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];

    if (x >= a.x && x <= b.x) {
      const span = b.x - a.x;

      return span <= 0 ? a.y : lerp(a.y, b.y, (x - a.x) / span);
    }
  }

  return last.y;
}

function destroySalesChart() {
  if (state.salesChart) {
    state.salesChart.destroy();
    state.salesChart = null;
  }
}

function destroyLookaheadChart() {
  if (state.lookaheadChart) {
    state.lookaheadChart.destroy();
    state.lookaheadChart = null;
  }
}

function destroyForecastChart() {
  if (state.forecastChart) {
    state.forecastChart.destroy();
    state.forecastChart = null;
  }
}

function parseExcelDate(rawValue, textValue) {
  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) {
    return rawValue;
  }

  // Same sanity window the string branch below applies: a header cell holding a
  // plain count rather than a date would otherwise become a 1900-something date.
  if (isExcelDateSerial(rawValue)) {
    return excelSerialToDate(rawValue);
  }

  const candidates = [String(rawValue || "").trim(), String(textValue || "").trim()];

  for (const text of candidates) {
    if (!text) {
      continue;
    }

    const serial = getOptionalNumber(text);

    if (isExcelDateSerial(serial)) {
      return excelSerialToDate(serial);
    }

    let match = text.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/);

    if (match) {
      return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    }

    match = text.match(/^(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})$/);

    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }

  return null;
}

function addMonths(date, months) {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = result.getDate();

  result.setMonth(result.getMonth() + months);

  if (result.getDate() !== day) {
    result.setDate(0);
  }

  return result;
}

// Excel serials for dates the workbook can plausibly carry: 20000 is 1954,
// 80000 is 2119. Anything outside that is a quantity, not a date.
function isExcelDateSerial(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 20000 && value < 80000;
}

function excelSerialToDate(serial) {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const date = new Date(utcValue * 1000);

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function dateToPeriodX(date, periods) {
  if (!date || !periods.length) {
    return null;
  }

  const y = date.getFullYear();
  const m = date.getMonth() + 1;

  if (periods[0].type === "month") {
    const i = periods.findIndex((p) => p.year === y && p.month === m);

    if (i < 0) {
      return null;
    }

    if (i >= periods.length - 1) {
      return i;
    }

    const days = new Date(y, m, 0).getDate();
    const fraction = clamp01((date.getDate() - 1) / days);

    return i + fraction;
  }

  const isoWeek = getIsoWeek(date);
  const isoYear = getIsoWeekYear(date);
  let i = periods.findIndex((p) => p.isoYear === isoYear && p.weekOrder === isoWeek);

  if (i < 0) {
    const sameMonth = periods
      .map((p, index) => ({ p, index }))
      .filter((x) => x.p.year === y && x.p.month === m);

    if (!sameMonth.length) {
      return null;
    }

    const days = new Date(y, m, 0).getDate();
    const pos = clamp01((date.getDate() - 1) / days);
    const bucket = Math.min(sameMonth.length - 1, Math.floor(pos * sameMonth.length));

    i = sameMonth[bucket].index;
  }

  if (i >= periods.length - 1) {
    return i;
  }

  const day = date.getDay();
  const mondayBased = day === 0 ? 6 : day - 1;
  const fraction = clamp01(mondayBased / 7);

  return i + fraction;
}

function dateToLookaheadX(date, periods) {
  if (!date || !periods.length) {
    return null;
  }

  const y = date.getFullYear();
  const m = date.getMonth() + 1;

  if (periods[0].type === "month") {
    const i = periods.findIndex((p) => p.year === y && p.month === m);

    if (i < 0) {
      return null;
    }

    const days = new Date(y, m, 0).getDate();
    const fraction = clamp01((date.getDate() - 1) / days);

    return i + fraction;
  }

  const isoWeek = getIsoWeek(date);
  const isoYear = getIsoWeekYear(date);
  let i = periods.findIndex((p) => p.isoYear === isoYear && p.weekOrder === isoWeek);

  if (i < 0) {
    const sameMonth = periods
      .map((p, index) => ({ p, index }))
      .filter((x) => x.p.year === y && x.p.month === m);

    if (!sameMonth.length) {
      return null;
    }

    const days = new Date(y, m, 0).getDate();
    const pos = clamp01((date.getDate() - 1) / days);
    const bucket = Math.min(sameMonth.length - 1, Math.floor(pos * sameMonth.length));

    i = sameMonth[bucket].index;
  }

  const day = date.getDay();
  const mondayBased = day === 0 ? 6 : day - 1;
  const fraction = clamp01(mondayBased / 7);

  return i + fraction;
}

function interpolateSeriesAtX(values, x) {
  if (!values.length) {
    return 0;
  }

  if (x <= 0) {
    return values[0];
  }

  const i = Math.floor(x);

  if (i >= values.length - 1) {
    return values[values.length - 1];
  }

  const t = x - i;

  return lerp(values[i], values[i + 1], t);
}

function getIsoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;

  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// ISO-week-year of a date (the year that owns this date's Thursday). Needed so
// a date can be matched to the right merged ISO-week period across the
// December/January boundary.
function getIsoWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;

  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // move to this week's Thursday

  return d.getUTCFullYear();
}

// The workbook files each weekly column under a calendar month, so an ISO week
// that straddles a month boundary is split into two partial columns (e.g. ISO
// week 18 appears under both April and May, week 1 under both December and
// January). The true week identity is (iso-week-year, week-number); derive the
// iso-week-year from the column's filed month + week number, handling the wrap.
function columnIsoWeekYear(year, month, week) {
  if (month === 1 && week >= 52) {
    return year - 1;
  }

  if (month === 12 && week <= 1) {
    return year + 1;
  }

  return year;
}

// Thursday of an ISO week. ISO weeks belong to the year/month of their Thursday;
// we use it to give each merged week a stable display month and a sort order.
function isoWeekThursday(isoYear, week) {
  const jan4 = new Date(isoYear, 0, 4); // Jan 4 is always in ISO week 1
  const jan4Dow = (jan4.getDay() + 6) % 7; // Monday = 0 ... Sunday = 6

  return new Date(isoYear, 0, 4 - jan4Dow + (week - 1) * 7 + 3);
}

// Merge weekly columns into true ISO-week periods. Boundary weeks that the
// workbook split across two months collapse into a single period here; their
// two partial columns are summed later in aggregateValueColumns. This removes
// the artificial dips/volatility caused by partial weeks at month edges.
function buildIsoWeekPeriods(weekCols) {
  const periodMap = new Map();

  for (const col of weekCols) {
    const week = normalizeWeekOrder(col.meta.weekOrOrder);

    if (!week) {
      continue;
    }

    const isoYear = columnIsoWeekYear(toNumber(col.meta.year), toNumber(col.meta.month), week);
    const key = `${isoYear}-W${String(week).padStart(2, "0")}`;

    if (periodMap.has(key)) {
      continue;
    }

    const thursday = isoWeekThursday(isoYear, week);

    periodMap.set(key, {
      type: "week",
      key,
      isoYear,
      weekOrder: week,
      year: thursday.getFullYear(),
      month: thursday.getMonth() + 1,
      label: `W${week}`,
    });
  }

  return Array.from(periodMap.values()).sort(
    (a, b) => a.isoYear * 100 + a.weekOrder - (b.isoYear * 100 + b.weekOrder)
  );
}

function monthKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function isValidPeriodMeta(meta) {
  const year = toNumber(meta?.year);
  const month = toNumber(meta?.month);

  return Number.isInteger(year) && year > 0 && Number.isInteger(month) && month >= 1 && month <= 12;
}

function normalizeWeekOrder(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const text = String(value || "");
  const match = text.match(/\d+/);

  if (!match) {
    return 0;
  }

  return Number(match[0]);
}

function getHeaderRow(headerRows, zeroBasedRowIndex) {
  return headerRows[zeroBasedRowIndex - 1] || [];
}

function aggregationLabel() {
  return state.aggregation === "sum" ? "Suma" : "Średnia";
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const text = String(value).replace(/\s/g, "").replace(",", ".");

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getOptionalNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const text = String(value).replace(/\s/g, "").replace(",", ".");

  if (!text) {
    return null;
  }

  const parsed = Number(text);

  return Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value) {
  return new Intl.NumberFormat("pl-PL", {
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function setStatus(message) {
  const el = document.getElementById("status");

  if (el) {
    el.textContent = message;
  }
}

function formatError(error) {
  if (!error) {
    return "Unknown";
  }

  if (error.debugInfo) {
    return JSON.stringify(error.debugInfo);
  }

  return error.message || String(error);
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function clamp01(value) {
  return clamp(value, 0, 1);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);

  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function getDeliveryMarkerColors(kind) {
  if (kind === "proposal") {
    return {
      dot: "rgba(124, 58, 237, 0.95)",
      stem: "rgba(124, 58, 237, 0.82)",
      fill: "rgba(237, 233, 254, 0.96)",
      stroke: "rgba(124, 58, 237, 0.95)",
      text: "rgba(91, 33, 182, 1)",
    };
  }

  return {
    dot: "rgba(37, 99, 235, 0.95)",
    stem: "rgba(37, 99, 235, 0.82)",
    fill: "rgba(219, 234, 254, 0.96)",
    stroke: "rgba(37, 99, 235, 0.95)",
    text: "rgba(30, 64, 175, 1)",
  };
}
